import { ModalPresence } from '../components/ModalPresence';
import { AnimatePresence, LayoutGroup } from 'motion/react';
import { ActiveNavigation, AnimatedSurface, useContentEntrance, useMotionPolicy } from '../components/Motion';
import { fileCategory } from '../../../server/lib/file-category';
import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import { Link, Navigate, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { BcktUploadSpinner } from '../components/BcktUploadSpinner';
import './storage-explorer.css';
import './account-navigation.css';
import { StorageDropdown } from '../components/StorageDropdown';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertCircle,
  Archive,
  Bell,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clipboard,
  Cloud,
  Code2,
  Copy,
  CreditCard,
  Download,
  ExternalLink,
  Eye,
  FolderPlus,
  Pause,
  Play,
  File,
  FileAudio,
  FileImage,
  FileVideo,
  Filter,
  Folder,
  Gauge,
  Globe2,
  KeyRound,
  LayoutDashboard,
  Link2,
  Lock,
  LogOut,
  List,
  Menu,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Settings,
  SlidersHorizontal,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { api, formatBytes } from '../api';
import { useAuth, type User } from '../auth';
import { Brand } from '../components/SiteChrome';
import { AccountSettings } from './AccountSettings';
import { AdminPanel } from './Admin';
import { WorkspacePeople } from './WorkspacePeople';
import { Affiliates } from './Affiliates';
import { CustomPlanBuilder } from './Pricing';
import { ToastProvider, useToast } from '../toast';


interface GlobalUploadItem {
  id: string;
  name: string;
  size: number;
  progress: number;
  speedMbps?: number | undefined;
  state: 'uploading' | 'done' | 'error' | 'aborted';
  abort?: (() => void) | undefined;
}

interface UploadContextValue {
  uploads: GlobalUploadItem[];
  uploadFiles: (files: FileList | File[], folder?: string, isPrivate?: boolean) => Promise<void>;
  clearUploads: () => void;
  cancelUpload: (id: string) => void;
}

const GlobalUploadContext = createContext<UploadContextValue | null>(null);

function useGlobalUpload() {
  const ctx = useContext(GlobalUploadContext);
  if (!ctx) throw new Error('useGlobalUpload must be used within GlobalUploadProvider');
  return ctx;
}

const dashboardNav = [
  { path: '/dashboard', label: 'Overview', icon: LayoutDashboard },
  { path: '/dashboard/storage', label: 'Storage', icon: Cloud },
  { path: '/dashboard/logs', label: 'Log Observatory', icon: Activity },
  { path: '/dashboard/credentials', label: 'API and S3', icon: KeyRound },
  { path: '/dashboard/workspace', label: 'Workspace', icon: Users },
  { path: '/dashboard/affiliates', label: 'Affiliates', icon: Link2 },
];

function UsageBar({
  label,
  used,
  limit,
  color,
}: {
  label: string;
  used: string;
  limit: string;
  color?: string;
}) {
  const percent = Number(limit) ? Math.min(100, (Number(used) / Number(limit)) * 100) : 0;
  return (
    <div className="usage-bar">
      <div>
        <span>{label}</span>
        <strong>
          {formatBytes(used)} <small>of {formatBytes(limit)}</small>
        </strong>
      </div>
      <div className="usage-bar__track">
        <i style={{ transform: `scaleX(${percent / 100})`, background: color }} />
      </div>
      <small>{percent.toFixed(1)}% used</small>
    </div>
  );
}


function RequestUsageBar({
  used,
  planCode,
}: {
  used: number | string;
  planCode: string;
}) {
  const planLimits: Record<string, number> = {
    free: 500_000,
    locked: 0,
    lite: 2_500_000,
    starter: 6_000_000,
    pro: 30_000_000,
    titan: 75_000_000,
    monster: 150_000_000,
    community_pro: 20_000_000,
    network_titan: 80_000_000,
  };
  const limit = planLimits[planCode] ?? 2_500_000;
  const current = Number(used || 0);
  const percent = Math.min(100, (current / limit) * 100);

  return (
    <div className="usage-bar">
      <div>
        <a
          href="/fair-use"
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            color: 'var(--color-ink-2)',
            textDecoration: 'none',
            fontSize: 'var(--text-sm)',
          }}
          title="Click to view our Fair Use Policy"
        >
          CDN Requests <small style={{ color: 'var(--color-muted)', textDecoration: 'underline' }}>(Fair Use)</small>
        </a>
        <strong>
          {current.toLocaleString()} <small>of {limit.toLocaleString()}</small>
        </strong>
      </div>
      <div className="usage-bar__track">
        <i
          style={{
            transform: `scaleX(${percent / 100})`,
            background: percent >= 90 ? '#f87171' : '#4ade80',
          }}
        />
      </div>
      <small>{percent.toFixed(1)}% of monthly guideline used</small>
    </div>
  );
}

function DashboardSidebar({ open, close }: { open: boolean; close: () => void }) {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [picker, setPicker] = useState(false);
  const [workspaceSearch, setWorkspaceSearch] = useState('');
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!picker) return;
    const outside = (event: PointerEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setPicker(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setPicker(false); pickerRef.current?.querySelector('button')?.focus(); }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [picker]);
  const workspaces = useQuery({
    queryKey: ['workspaces'],
    queryFn: () =>
      api<{
        current_owner_user_id: string;
        workspaces: Array<{
          owner_user_id: string;
          name: string;
          avatar_url?: string | null;
          owner_display_name: string;
          role: string;
          is_current?: boolean;
        }>;
      }>('/workspace'),
  });
  const currentWorkspace =
    workspaces.data?.workspaces.find((item) => item.is_current) ?? workspaces.data?.workspaces[0];
  return (
    <aside className={open ? 'dash-sidebar is-open' : 'dash-sidebar'}>
      <div className="dash-sidebar__top">
        <Brand />
        <button className="dash-sidebar__close" onClick={close} aria-label="Close navigation">
          <X />
        </button>
      </div>
      <div className="workspace-switch-wrap" ref={pickerRef} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setPicker(false); }}>
      <button className="workspace-switch" aria-expanded={picker} aria-controls="workspace-picker" onClick={() => { setPicker((value) => !value); setWorkspaceSearch(''); }}>
        <span>
          {currentWorkspace?.avatar_url ? (
            <img src={currentWorkspace.avatar_url} alt="" />
          ) : (
            (currentWorkspace?.name ?? user?.display_name)?.slice(0, 1).toUpperCase()
          )}
        </span>
        <div>
          <strong>{currentWorkspace?.name ?? user?.display_name}</strong>
          <small>{currentWorkspace?.role === 'owner' || !currentWorkspace ? 'Personal workspace' : currentWorkspace.role}</small>
        </div>
        <ChevronDown />
      </button>
      <AnimatePresence initial={false}>{picker && (
        <AnimatedSurface origin="top left" key="workspaces" className="workspace-picker workspace-picker--refined" id="workspace-picker">
          <header>Workspaces <span>{workspaces.data?.workspaces.length ?? 0}</span></header>
          <label className="workspace-picker__search"><Search /><input autoFocus aria-label="Search workspaces" placeholder="Find a workspace" value={workspaceSearch} onChange={(event) => setWorkspaceSearch(event.target.value)} /></label>
          <div className="workspace-picker__list">
          {workspaces.data?.workspaces.filter((item) => item.name.toLowerCase().includes(workspaceSearch.toLowerCase())).map((item) => (
            <button
              disabled={switching}
              aria-current={item.is_current ? 'true' : undefined}
              key={item.owner_user_id}
              className={item.is_current ? 'is-current' : undefined}
              onClick={async () => {
                if (item.is_current) { setPicker(false); return; }
                setSwitching(true);
                setSwitchError('');
                try {
                await api('/workspace/switch', {
                  method: 'POST',
                  body: JSON.stringify({ owner_user_id: item.owner_user_id }),
                });
                setPicker(false);
                await client.invalidateQueries();
                await refresh();
                } catch (error) { setPicker(true); setSwitchError(error instanceof Error ? error.message : 'Unable to switch workspace.'); }
                finally { setSwitching(false); }
              }}
            >
              {item.avatar_url ? <img src={item.avatar_url} alt="" /> : <i>{item.name.slice(0, 1).toUpperCase()}</i>}
              <strong>{item.name}</strong>
              <small>{item.role === 'owner' ? 'Your workspace' : `${item.owner_display_name} · ${item.role}`}</small>
              {item.is_current && <Check className="workspace-picker__check" />}
            </button>
          ))}
          {workspaces.isPending && <p>Loading workspaces…</p>}
          {workspaces.isError && <button onClick={() => void workspaces.refetch()}>Retry loading workspaces</button>}
          {workspaces.data && !workspaces.data.workspaces.some((item) => item.name.toLowerCase().includes(workspaceSearch.toLowerCase())) && <p>No workspaces found.</p>}
          </div>
          {switchError && <p role="alert">{switchError}</p>}
          <Link className="workspace-picker__manage" to="/dashboard/workspace" onClick={() => { setPicker(false); close(); }}><Users />Manage workspace<ChevronRight /></Link>
        </AnimatedSurface>
      )}</AnimatePresence>
      </div>
      <LayoutGroup id="dashboard-navigation"><nav>
        {dashboardNav
          .filter((item) => {
            if (item.path.endsWith('/credentials')) return user?.workspace?.permissions.credentials !== false;
            if (item.path.endsWith('/billing')) return user?.workspace?.permissions.billing !== false;
            if (item.path.endsWith('/workspace')) return user?.workspace?.permissions.members !== false;
            return true;
          })
          .map((item) => (
          <NavLink end={item.path === '/dashboard'} to={item.path} key={item.path} onClick={close}>
            {({ isActive }) => <><ActiveNavigation active={isActive} id="sidebar-active" /><item.icon />{item.label}</>}
          </NavLink>
        ))}
        {user?.admin_permissions?.some((permission) => permission === '*' || permission === 'admin.access') && (
          <NavLink to="/dashboard/admin" onClick={close}>
            {({ isActive }) => <><ActiveNavigation active={isActive} id="sidebar-active" /><ShieldCheck />Administration</>}
          </NavLink>
        )}
      </nav></LayoutGroup>
      <div className="dash-sidebar__bottom">
        <Link to="/docs">
          <CircleHelp />
          Documentation
        </Link>
        <a href="https://status.bckt.io" target="_blank" rel="noopener noreferrer">
          <Activity />
          Status
        </a>
        <button
          onClick={async () => {
            await logout();
            navigate('/');
          }}
        >
          <LogOut />
          Sign out
        </button>
        <div className="mini-usage-group">
          <div className="mini-usage">
            <span>Media storage</span>
            <i>
              <b
                style={{
                  transform: `scaleX(${Math.min(1, Number(user?.storage_used_bytes ?? 0) / Number(user?.storage_quota_bytes ?? 1))})`,
                }}
              />
            </i>
            <small>
              {formatBytes(user?.storage_used_bytes ?? 0)} /{' '}
              {formatBytes(user?.storage_quota_bytes ?? 0)}
            </small>
          </div>
          <div className="mini-usage mini-usage--logs">
            <span>Log storage</span>
            <i>
              <b
                style={{
                  transform: `scaleX(${Math.min(1, Number(user?.log_used_bytes ?? 0) / Number(user?.log_quota_bytes ?? 1))})`,
                }}
              />
            </i>
            <small>
              {formatBytes(user?.log_used_bytes ?? 0)} / {formatBytes(user?.log_quota_bytes ?? 0)}
            </small>
          </div>
        </div>
      </div>
    </aside>
  );
}

function DashboardTopbar({ title, openMenu }: { title: string; openMenu: () => void }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<'notifications' | 'account' | null>(null);
  const actions = useRef<HTMLDivElement>(null);
  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      api<{
        unread: number;
        notifications: Array<{
          id: string;
          type: string;
          title: string;
          message: string;
          read_at: string | null;
          created_at: string;
        }>;
      }>('/notifications'),
    refetchInterval: 60_000,
  });
  const readNotifications = useMutation({
    mutationFn: () => api<void>('/notifications/read', { method: 'POST' }),
    onSuccess: () => void notifications.refetch(),
  });

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!actions.current?.contains(event.target as Node)) setPanel(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPanel(null);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', escape);
    };
  }, []);

  const signOut = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="dash-topbar">
      <button className="dash-menu" onClick={openMenu} aria-label="Open navigation">
        <Menu />
      </button>
      <div>
        <span>Workspace</span>
        <strong>{title}</strong>
      </div>
      <div className="dash-topbar__actions" ref={actions}>
        <button
          className="icon-button"
          aria-label="Notifications"
          aria-expanded={panel === 'notifications'}
          onClick={() =>
            setPanel((current) => {
              const next = current === 'notifications' ? null : 'notifications';
              if (next === 'notifications' && (notifications.data?.unread ?? 0) > 0)
                readNotifications.mutate();
              return next;
            })
          }
        >
          <Bell />
          {(notifications.data?.unread ?? 0) > 0 ? <i /> : null}
        </button>
        <button
          className="avatar-button"
          aria-label="Account menu"
          aria-expanded={panel === 'account'}
          onClick={() => setPanel((current) => (current === 'account' ? null : 'account'))}
        >
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" />
          ) : (
            user?.display_name?.slice(0, 1).toUpperCase()
          )}
        </button>
        <AnimatePresence initial={false}>{panel === 'notifications' && (
          <AnimatedSurface key="notifications"
            className="dash-popover dash-popover--notifications"
            role="dialog"
            aria-label="Notifications"
          >
            <div>
              <strong>Notifications</strong>
              <span>{notifications.data?.unread ?? 0} unread</span>
            </div>
            {notifications.data?.notifications.length ? (
              <div className="notification-list">
                {notifications.data.notifications.map((item) => (
                  <article className={item.read_at ? '' : 'is-unread'} key={item.id}>
                    <span>{item.type.startsWith('billing.') ? <CreditCard /> : <Bell />}</span>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.message}</p>
                      <time>
                        {new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
                          -Math.max(
                            0,
                            Math.floor(
                              (Date.now() - new Date(item.created_at).getTime()) / 86_400_000,
                            ),
                          ),
                          'day',
                        )}
                      </time>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="dash-popover__empty">
                <Bell />
                <strong>No new notifications</strong>
                <p>Important account, billing, and storage updates will appear here.</p>
              </div>
            )}
          </AnimatedSurface>
        )}
        {panel === 'account' && (
          <AnimatedSurface key="account" className="dash-popover dash-popover--account" role="menu">
            <div className="account-summary">
              <span>
                {user?.avatar_url ? <img src={user.avatar_url} alt="" /> : user?.display_name?.slice(0, 1).toUpperCase()}
              </span>
              <div>
                <strong>{user?.display_name}</strong>
                <small>{user?.email}</small>
              </div>
            </div>
            <Link role="menuitem" to="/dashboard/billing" onClick={() => setPanel(null)}>
              <CreditCard />
              Usage and billing
            </Link>
            <Link role="menuitem" to="/dashboard/settings" onClick={() => setPanel(null)}>
              <SlidersHorizontal />
              Account settings
            </Link>
            <button role="menuitem" onClick={() => void signOut()}>
              <LogOut />
              Sign out
            </button>
          </AnimatedSurface>
        )}</AnimatePresence>
      </div>
    </header>
  );
}

function StorageGraceBanner({ user }: { user: User }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  if (user.plan_code === 'locked' || !user.storage_grace_deadline || !user.storage_grace_reason) return null;
  const days = Math.max(
    0,
    Math.ceil((new Date(user.storage_grace_deadline).getTime() - now) / 86_400_000),
  );
  const excess = Math.max(0, Number(user.storage_used_bytes) - Number(user.storage_quota_bytes));
  const title =
    user.storage_grace_reason === 'payment_failed'
      ? 'Your subscription payment could not be renewed.'
      : user.storage_grace_reason === 'subscription_expired'
        ? 'Your subscription has expired.'
        : 'Your storage exceeds the allowance of your new plan.';
  return (
    <div className="storage-grace-banner" role="alert">
      <AlertCircle />
      <div>
        <strong>{title}</strong>
        <span>
          {excess > 0 ? `Free ${formatBytes(excess)} of storage. ` : ''}The oldest files above your
          allowance will be deleted automatically in {days} {days === 1 ? 'day' : 'days'}.
        </span>
      </div>
      <Link to="/dashboard/billing">Review billing</Link>
    </div>
  );
}

function FairUseGraceBanner({ user }: { user: User }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  if (user.fair_use_paused_at || (user.fair_use_grace_deadline && new Date(user.fair_use_grace_deadline).getTime() <= now)) {
    return (
      <div className="storage-grace-banner is-paused" role="alert">
        <AlertCircle />
        <div>
          <strong>Uploads are currently paused under our Fair Use policy.</strong>
          <span>
            Your workspace reached its monthly delivery request guideline and the 7-day grace period has ended.
            Existing files remain online and served normally. Upgrade your plan or wait for your next billing renewal to resume uploads.{' '}
            <a href="/fair-use" target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>
              Questions? View our Fair Use Policy
            </a>
          </span>
        </div>
        <Link to="/pricing">Upgrade plan</Link>
      </div>
    );
  }

  if (!user.fair_use_grace_deadline) return null;

  const days = Math.max(
    0,
    Math.ceil((new Date(user.fair_use_grace_deadline).getTime() - now) / 86_400_000),
  );

  return (
    <div className="storage-grace-banner" role="alert">
      <AlertCircle />
      <div>
        <strong>Monthly delivery request guideline reached.</strong>
        <span>
          You have a grace period of <strong>{days} {days === 1 ? "day remaining" : "days remaining"}</strong> to upgrade your plan before new uploads are paused until your next billing renewal. All existing files continue to be delivered normally without interruption.{' '}
          <a href="/fair-use" target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>
            Questions? View our Fair Use Policy
          </a>
        </span>
      </div>
      <Link to="/pricing">Upgrade plan</Link>
    </div>
  );
}

function LockedPlanBanner({ user }: { user: User }) {
  if (user.plan_code !== 'locked') return null;
  const copy =
    user.signup_plan_reason === 'discord_free'
      ? 'This Discord account already has a free workspace. Choose a plan to activate this one.'
      : 'This workspace is waiting for a paid plan because a free workspace is already registered on this network.';
  return (
    <div className="locked-plan-banner" role="status">
      <Lock />
      <div>
        <strong>This workspace needs a paid plan.</strong>
        <span>{copy} Uploads, keys and invites stay closed until checkout completes.</span>
      </div>
      <Link to="/pricing">Choose a plan</Link>
    </div>
  );
}

interface WorkspaceEvent {
  id: string;
  event_type: string;
  title: string;
  description?: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface ActivityDay {
  date: string;
  files: number;
  logs: string;
}

function MetricTrend({
  days,
  value,
  label,
}: {
  days: ActivityDay[];
  value: 'files' | 'logs';
  label: string;
}) {
  const values = days.map((day) => Number(day[value]));
  const maximum = Math.max(1, ...values);
  return (
    <div className="metric-trend" role="img" aria-label={label}>
      {days.map((day, index) => {
        const amount = values[index] ?? 0;
        return (
          <span key={day.date} title={`${day.date}: ${amount.toLocaleString('en-US')}`}>
            <i
              style={{ transform: `scaleY(${amount ? Math.max(0.08, amount / maximum) : 0.02})`, animationDelay: `${index * 45}ms` }}
            />
            <small>
              {new Intl.DateTimeFormat('en', { weekday: 'narrow', timeZone: 'UTC' }).format(
                new Date(`${day.date}T00:00:00Z`),
              )}
            </small>
          </span>
        );
      })}
    </div>
  );
}

function relativeTime(value: string) {
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, 'second');
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');
  return formatter.format(Math.round(hours / 24), 'day');
}

function Overview({ user }: { user: User }) {
  const notify = useToast();
  const { instant } = useMotionPolicy();
  const [animateEntrance] = useState(() => !instant);
  const [activityMenu, setActivityMenu] = useState(false);
  const [activityLimit, setActivityLimit] = useState(8);
  const [cdnRange, setCdnRange] = useState<'24h' | '7d' | '14d' | '30d' | '90d' | '180d' | '365d'>('30d');
  const activity = useQuery({
    queryKey: ['workspace-activity', activityLimit, cdnRange],
    queryFn: () =>
      api<{
        events: WorkspaceEvent[];
        summary: {
          stored_files: number;
          logs_24h: string;
          logs_7d: string;
          cdn_requests_range?: string;
          cdn_requests_total?: string;
        };
        series: ActivityDay[];
      }>(`/activity?limit=${activityLimit}&range=${cdnRange}`),
  });

  const refreshActivity = async () => {
    setActivityMenu(false);
    const result = await activity.refetch();
    notify(
      result.isError
        ? { title: 'Activity could not be refreshed', tone: 'error' }
        : { title: 'Workspace activity refreshed', tone: 'success' },
    );
  };

  return (
    <div className="dashboard-page overview-page" data-entrance={animateEntrance ? 'animated' : undefined}>
      <div className="dash-heading">
        <div className="dash-heading__copy">
          <p>Good afternoon, {user.display_name.split(' ')[0]}.</p>
          <h1>
            {user.plan_code === 'locked'
              ? 'This workspace is waiting for a plan.'
              : 'Everything is where you left it.'}
          </h1>
        </div>
        <div className="dash-heading__actions">
          <Link
            className="button button--dash-primary"
            to={user.plan_code === 'locked' ? '/pricing' : '/dashboard/storage'}
          >
            <Upload />
            {user.plan_code === 'locked' ? 'Choose a plan' : 'Upload files'}
          </Link>
        </div>
      </div>
      <div className="overview-grid">
        <article className="dash-card dash-card--usage">
          <div className="dash-card__head">
            <div>
              <span>Capacity</span>
              <h2>Workspace usage</h2>
            </div>
            <Gauge />
          </div>
          <UsageBar
            label="Media storage"
            used={user.storage_used_bytes}
            limit={user.storage_quota_bytes}
          />
          <UsageBar
            label="Log storage"
            used={user.log_used_bytes}
            limit={user.log_quota_bytes}
            color="var(--color-info)"
          />
          <RequestUsageBar
            used={user.file_requests_count ?? 0}
            planCode={user.plan_code}
          />
        </article>
        <article className="dash-card metric-card">
          <span>Stored files</span>
          {activity.isLoading ? (
            <span className="metric-value-skeleton" />
          ) : (
            <strong>{activity.data?.summary.stored_files ?? 0}</strong>
          )}
          <small>Total active objects</small>
          <MetricTrend
            days={activity.data?.series ?? []}
            value="files"
            label="Files uploaded during the last 7 days"
          />
        </article>
        <article className="dash-card metric-card">
          <span>Logs ingested</span>
          {activity.isLoading ? (
            <span className="metric-value-skeleton" />
          ) : (
            <strong>{activity.data?.summary.logs_7d ?? '0'}</strong>
          )}
          <small>Events accepted in the last 7 days</small>
          <MetricTrend
            days={activity.data?.series ?? []}
            value="logs"
            label="Log events ingested during the last 7 days"
          />
        </article>
        <article className="dash-card metric-card" style={{ position: 'relative' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>CDN Requests</span>
            <select
              value={cdnRange}
              onChange={(e) => setCdnRange(e.target.value as any)}
              style={{
                background: 'var(--color-paper-3)',
                border: '1px solid var(--color-rule)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-ink)',
                fontSize: '11px',
                fontWeight: 500,
                padding: '3px 8px',
                cursor: 'pointer',
                outline: 'none',
              }}
              aria-label="Filter CDN request period"
            >
              <option value="24h">Last 24h</option>
              <option value="7d">Last 7 days</option>
              <option value="14d">Last 14 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="180d">Last 180 days</option>
              <option value="365d">Last year</option>
            </select>
          </div>
          {activity.isLoading ? (
            <span className="metric-value-skeleton" />
          ) : (
            <strong>{Number(activity.data?.summary.cdn_requests_range ?? 0).toLocaleString()}</strong>
          )}
          <small>
            In {cdnRange} · {Number(activity.data?.summary.cdn_requests_total ?? 0).toLocaleString()} total requests
          </small>
        </article>
      </div>
      <div className="overview-bottom">
        <article className="dash-card activity-card">
          <div className="dash-card__head">
            <div>
              <span>Recent activity</span>
              <h2>Workspace events</h2>
            </div>
            <button
              className="icon-button"
              aria-label="Workspace event actions"
              aria-expanded={activityMenu}
              onClick={() => setActivityMenu((open) => !open)}
            >
              <MoreHorizontal />
            </button>
            {activityMenu && (
              <div className="activity-menu">
                <button onClick={() => void refreshActivity()}>
                  <RefreshCw />
                  Refresh activity
                </button>
                <button
                  onClick={() => {
                    setActivityLimit((limit) => (limit === 8 ? 50 : 8));
                    setActivityMenu(false);
                  }}
                >
                  <Activity />
                  {activityLimit === 8 ? 'Show more events' : 'Show recent events'}
                </button>
              </div>
            )}
          </div>
          {activity.isLoading ? (
            <div className="activity-skeleton" aria-label="Loading workspace activity" />
          ) : activity.isError ? (
            <div className="empty-state">
              <AlertCircle />
              <h3>Activity is unavailable</h3>
              <p>Refresh the card to try loading workspace events again.</p>
            </div>
          ) : activity.data?.events.length ? (
            <div className="activity-list">
              {activity.data.events.map((event) => (
                <div className="activity-row" key={event.id}>
                  <span className="activity-row__icon">
                    {event.event_type.startsWith('file.') ? (
                      event.event_type === 'file.deleted' ? (
                        <Trash2 />
                      ) : (
                        <Upload />
                      )
                    ) : event.event_type.startsWith('credential.') ? (
                      <KeyRound />
                    ) : event.event_type.startsWith('billing.') ? (
                      <CreditCard />
                    ) : event.event_type.startsWith('logs.') ? (
                      <Activity />
                    ) : (
                      <ShieldCheck />
                    )}
                  </span>
                  <span>
                    <strong>{event.title}</strong>
                    {event.description && <small>{event.description}</small>}
                  </span>
                  <time dateTime={event.created_at}>{relativeTime(event.created_at)}</time>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Activity />
              <h3>No activity yet</h3>
              <p>Your uploads, deletions, key changes, and billing events will appear here.</p>
            </div>
          )}
        </article>
        <article className="dash-card quickstart-card">
          <div>
            <span>Quickstart</span>
            <h2>Put your first object on the edge.</h2>
            <p>Create a scoped API key and upload a file with one request.</p>
          </div>
          <ol>
            <li>
              <span>1</span>
              <Link to="/dashboard/credentials">Create an API key</Link>
            </li>
            <li>
              <span>2</span>
              <Link to="/docs#uploads">Send an upload</Link>
            </li>
            <li>
              <span>3</span>
              <Link to="/dashboard/storage">Copy the CDN URL</Link>
            </li>
          </ol>
        </article>
      </div>
    </div>
  );
}

interface FileRecord {
  id: string;
  public_key: string;
  original_name: string;
  folder: string;
  size_bytes: string;
  mime_type: string;
  sha256: string;
  moderation_status: string;
  is_private: boolean;
  created_at: string;
  url: string;
}

interface FolderRecord {
  name: string;
  path: string;
  file_count: number;
  size_bytes: string;
}

function FileTypeIcon({ mimeType, name }: { mimeType: string; name: string }) {
  const category = fileCategory(mimeType, name);
  if (category === 'image') return <FileImage />;
  if (category === 'video') return <FileVideo />;
  if (category === 'audio') return <FileAudio />;
  if (mimeType.includes('zip') || mimeType.includes('archive')) return <Archive />;
  return <File />;
}

function DashboardDataSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="dashboard-data-skeleton" aria-label="Loading data">
      {Array.from({ length: rows }, (_, index) => (
        <span key={index} />
      ))}
    </div>
  );
}

function formatLogTick(date: Date, rangeHours: number) {
  if (rangeHours <= 6) {
    return new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
  }
  if (rangeHours <= 24) {
    return new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
  }
  if (rangeHours <= 168) {
    return new Intl.DateTimeFormat('en', { weekday: 'short', hour: '2-digit', hour12: false }).format(date);
  }
  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(date);
}

function formatLogBucket(date: Date, rangeHours: number) {
  if (rangeHours <= 24) {
    return new Intl.DateTimeFormat('en', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  }
  if (rangeHours <= 168) {
    return new Intl.DateTimeFormat('en', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  }
  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(date);
}

function LogTrend({
  timeline,
  from,
  to,
  rangeHours,
}: {
  timeline: number[];
  from: number;
  to: number;
  rangeHours: number;
}) {
  const buckets = Math.max(1, timeline.length);
  const spanMs = Math.max(1, to - from);
  const bucketMs = spanMs / buckets;
  const points = timeline.map((events, index) => {
    const start = new Date(from + index * bucketMs);
    return { events, start, label: formatLogBucket(start, rangeHours) };
  });
  const maximum = Math.max(1, ...points.map((point) => point.events));
  const ticks = [points[0], points[Math.floor((points.length - 1) / 2)], points.at(-1)].filter(
    (point, index, list): point is NonNullable<typeof point> => Boolean(point) && list.indexOf(point) === index,
  );

  if (!points.length) {
    return (
      <div className="log-trend" role="img" aria-label="No matching log events in the selected time range">
        <div className="log-trend__empty">No matching events in this window</div>
      </div>
    );
  }

  return (
    <div className="log-trend" role="img" aria-label={`Matching log events over the last ${rangeHours} hours`}>
      <div className="log-trend__bars">
        {points.map((point) => (
          <span key={point.start.toISOString()}>
            <i style={{ transform: `scaleY(${point.events ? Math.max(0.08, point.events / maximum) : 0.02})` }} />
            <em className="log-trend__tip">
              <strong>{`${point.events} event${point.events === 1 ? '' : 's'}`}</strong>
              <small>{point.label}</small>
            </em>
          </span>
        ))}
      </div>
      <div className="log-trend__axis">
        {ticks.map((point) => (
          <span key={point.start.toISOString()}>{formatLogTick(point.start, rangeHours)}</span>
        ))}
      </div>
    </div>
  );
}

function StorageManager() {
  const client = useQueryClient();
  const notify = useToast();
  const { user } = useAuth();
  const [folder, setFolder] = useState('');
  const [search, setSearch] = useState('');
  const fileSearchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || target?.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="alertdialog"]') || document.querySelector('.secret-modal')) return;
      event.preventDefault();
      fileSearchRef.current?.focus();
    };
    document.addEventListener('keydown', focusSearch);
    return () => document.removeEventListener('keydown', focusSearch);
  }, []);
  const [dragging, setDragging] = useState(false);
  const canWrite = user?.workspace?.permissions.filesWrite !== false;
  const canDelete = user?.workspace?.permissions.filesDelete ?? canWrite;
  const canPrivacy = user?.workspace?.permissions.filesPrivacy ?? canWrite;
  const canCreateFolders = user?.workspace?.permissions.foldersCreate !== false;
  const canDeleteFolders = user?.workspace?.permissions.foldersDelete ?? canWrite;
  const workspaceLocked = user?.plan_code === 'locked';
  const canUpload = canWrite && !workspaceLocked;
  const canManageFolders = canCreateFolders && !workspaceLocked;
  const { uploadFiles } = useGlobalUpload();
  const [copied, setCopied] = useState('');
  const [uploadPrivate, setUploadPrivate] = useState(false);
  const [accessFilter, setAccessFilter] = useState<'all' | 'public' | 'private'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'image' | 'video' | 'audio' | 'other'>(
    'all',
  );
  const [sort, setSort] = useState<'newest' | 'oldest' | 'name' | 'size'>('newest');
  const [purgeModalOpen, setPurgeModalOpen] = useState(false);
  const [purgeEnabled, setPurgeEnabled] = useState(false);
  const [purgeFolder, setPurgeFolder] = useState("");
  const [purgeMatchType, setPurgeMatchType] = useState<"all" | "starts_with" | "contains" | "ends_with">("all");
  const [purgeMatchValue, setPurgeMatchValue] = useState("");

  const purgeSettingsQuery = useQuery({
    queryKey: ["storage-auto-purge"],
    queryFn: async () => {
      const res = await api<{ success: boolean; settings: { enabled: boolean; folder: string; match_type: "all" | "starts_with" | "contains" | "ends_with"; match_value: string } }>("/files/storage/auto-purge");
      return res.settings;
    },
  });

  useEffect(() => {
    if (purgeSettingsQuery.data) {
      setPurgeEnabled(purgeSettingsQuery.data.enabled);
      setPurgeFolder(purgeSettingsQuery.data.folder || "");
      setPurgeMatchType(purgeSettingsQuery.data.match_type || "all");
      setPurgeMatchValue(purgeSettingsQuery.data.match_value || "");
    }
  }, [purgeSettingsQuery.data]);

  const savePurgeMutation = useMutation({
    mutationFn: async (payload: { enabled: boolean; folder: string; match_type: string; match_value: string }) => {
      return api("/files/storage/auto-purge", {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["storage-auto-purge"] });
      notify({ title: "Storage settings saved", tone: "success" });
      setPurgeModalOpen(false);
    },
    onError: () => {
      notify({ title: "Failed to save storage settings", tone: "error" });
    },
  });
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [compact, setCompact] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [deleteTarget, setDeleteTarget] = useState<FileRecord | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<FolderRecord | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const files = useQuery({
    queryKey: ['files', folder, search, accessFilter, typeFilter, sort, page],
    queryFn: () => {
      const parameters = new URLSearchParams({
        folder,
        search,
        access: accessFilter,
        type: typeFilter,
        sort,
        page: String(page),
        limit: String(pageSize),
      });
      return api<{
        folders: FolderRecord[];
        files: FileRecord[];
        pagination: { page: number; per_page: number; total: number; total_pages: number };
      }>(`/files?${parameters.toString()}`);
    },
    placeholderData: (previous) => previous,
  });
  const visibleFolders = files.data?.folders ?? [];
  const visibleFiles = files.data?.files ?? [];
  const pagination = files.data?.pagination;
  const selectedFile = visibleFiles.find((file) => file.id === selectedId) ?? null;
  const resultsRef = useRef<HTMLDivElement>(null);
  useContentEntrance(resultsRef, `${folder}:${view}:${sort}:${page}:${files.dataUpdatedAt}`);
  const breadcrumb = folder.split('/').filter(Boolean);

  useEffect(() => {
    if (selectedId && !selectedFile) setSelectedId('');
  }, [selectedFile, selectedId]);
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [folder, search, accessFilter, typeFilter, sort]);
  const createFolder = useMutation({
    mutationFn: async (name: string) => {
      const cleanName = name.trim().replaceAll('/', '').replaceAll('\\', '');
      if (!cleanName) throw new Error('Enter a folder name.');
      const path = folder ? `${folder}/${cleanName}` : cleanName;
      const token = await api<{ token: string }>('/auth/upload-token', { method: 'POST' });
      const response = await fetch(
        `${import.meta.env.VITE_UPLOAD_URL ?? 'https://upload.bckt.io'}/v1/folders?folder=${encodeURIComponent(path)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token.token}` } },
      );
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as any;
        throw new Error(payload?.error?.message ?? 'The folder could not be created.');
      }
      return path;
    },
    onSuccess: (path) => {
      setCreatingFolder(false);
      setFolderName('');
      void client.invalidateQueries({ queryKey: ['files'] });
      void client.invalidateQueries({ queryKey: ['folders'] });
      notify({ title: 'Folder created', description: path, tone: 'success' });
    },
    onError: (error: Error) =>
      notify({ title: 'Folder could not be created', description: error.message, tone: 'error' }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/files/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteTarget(null);
      setSelectedId('');
      void client.invalidateQueries({ queryKey: ['files'] });
      void client.invalidateQueries({ queryKey: ['folders'] });
      void client.invalidateQueries({ queryKey: ['me'] });
      void client.invalidateQueries({ queryKey: ['workspace-activity'] });
      notify({
        title: 'File deleted',
        description: 'The object was removed from storage.',
        tone: 'success',
      });
    },
    onError: () => notify({ title: 'File could not be deleted', tone: 'error' }),
  });
  const removeFolder = useMutation({
    mutationFn: (path: string) =>
      api<{ deleted_objects: number }>(`/files/folders?path=${encodeURIComponent(path)}`, {
        method: 'DELETE',
      }),
    onSuccess: (result) => {
      setDeleteFolderTarget(null);
      void client.invalidateQueries({ queryKey: ['files'] });
      void client.invalidateQueries({ queryKey: ['folders'] });
      void client.invalidateQueries({ queryKey: ['me'] });
      notify({
        title: 'Folder deleted',
        description: `${result.deleted_objects} objects were permanently removed.`,
        tone: 'success',
      });
    },
    onError: () => notify({ title: 'Folder could not be deleted', tone: 'error' }),
  });
  const removeSelected = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => api(`/files/${id}`, { method: 'DELETE' })),
      );
      const deleted = results.filter((result) => result.status === 'fulfilled').length;
      if (deleted !== ids.length)
        throw Object.assign(new Error('Some files could not be deleted'), { deleted });
      return deleted;
    },
    onSuccess: (deleted) => {
      setSelectedIds(new Set());
      setSelectedId('');
      setConfirmBulkDelete(false);
      void client.invalidateQueries({ queryKey: ['files'] });
      void client.invalidateQueries({ queryKey: ['folders'] });
      void client.invalidateQueries({ queryKey: ['me'] });
      void client.invalidateQueries({ queryKey: ['workspace-activity'] });
      notify({
        title: `${deleted} ${deleted === 1 ? 'file' : 'files'} deleted`,
        description: 'The selected objects were removed from storage.',
        tone: 'success',
      });
    },
    onError: (error: Error & { deleted?: number }) => {
      setConfirmBulkDelete(false);
      void client.invalidateQueries({ queryKey: ['files'] });
      void client.invalidateQueries({ queryKey: ['folders'] });
      notify({
        title: 'Some files could not be deleted',
        description: error.deleted
          ? `${error.deleted} objects were removed before the error.`
          : 'Nothing was changed.',
        tone: 'error',
      });
    },
  });
  const toggleSelected = (id: string) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allPageSelected =
    visibleFiles.length > 0 && visibleFiles.every((file) => selectedIds.has(file.id));
  const copyFileLink = async (file: FileRecord) => {
    try {
      const access = file.is_private
        ? await api<{ url: string; expires_at: string | null }>(`/files/${file.id}/access-url`, {
            method: 'POST',
          })
        : { url: file.url, expires_at: null };
      await navigator.clipboard.writeText(access.url);
      setCopied(file.id);
      window.setTimeout(() => setCopied(''), 1000);
      notify({
        title: file.is_private ? 'Private link copied' : 'CDN URL copied',
        description: file.is_private ? 'This link expires in one hour.' : file.original_name,
        tone: 'success',
      });
    } catch {
      notify({ title: 'The CDN URL could not be copied', tone: 'error' });
    }
  };
  const visibility = useMutation({
    mutationFn: ({ id, isPrivate }: { id: string; isPrivate: boolean }) =>
      api(`/files/${id}/visibility`, {
        method: 'PATCH',
        body: JSON.stringify({ is_private: isPrivate }),
      }),
    onSuccess: (_result, variables) => {
      void client.invalidateQueries({ queryKey: ['files'] });
      void client.invalidateQueries({ queryKey: ['workspace-activity'] });
      notify({
        title: variables.isPrivate ? 'File is now private' : 'File is now public',
        description: variables.isPrivate
          ? 'Public access has been disabled.'
          : 'The permanent CDN URL is available again.',
        tone: 'success',
      });
    },
    onError: () => notify({ title: 'File visibility could not be changed', tone: 'error' }),
  });
  const handleUpload = (files: FileList | File[]) => {
    void uploadFiles(files, folder, uploadPrivate);
  };
  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    void handleUpload(event.dataTransfer.files);
  };
  if (files.isLoading && !files.data)
    return (
      <div className="dashboard-page storage-page">
        <div className="dash-heading">
          <div className="dash-heading__copy">
            <h1>Storage</h1>
            <p className="storage-heading-description">Manage files and delivery.</p>
          </div>
        </div>
        <div className="storage-loading-shell" aria-label="Loading file explorer">
          <DashboardDataSkeleton rows={9} />
        </div>
      </div>
    );
  return (
    <div className={`dashboard-page storage-page${compact ? ' is-compact' : ''}`}>
      <div className="storage-layout">
        <section
          className={`file-panel${dragging ? ' is-dragging' : ''}${files.isFetching ? ' is-refreshing' : ''}`}
          aria-busy={files.isFetching}
          onKeyDown={(event) => { if (event.key === 'Escape') setSelectedId(''); }}
          onDragOver={(event) => {
            event.preventDefault();
            if (canUpload) setDragging(true);
          }}
          onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false); }}
          onDrop={canUpload ? onDrop : undefined}
        >
          <div className="file-panel__path">
            <button className="storage-up" type="button" aria-label="Go to parent folder" disabled={!folder} onClick={() => setFolder(breadcrumb.slice(0, -1).join('/'))}><ChevronLeft size={16} /></button>
            <nav aria-label="Current folder">
              <button type="button" className={!folder ? 'is-current' : ''} onClick={() => setFolder('')}>
                <Cloud size={15} /> All files
              </button>
              {breadcrumb.map((segment, index) => (
                <span key={`${segment}-${index}`}>
                  <span>/</span>
                  <button
                    type="button"
                    className={index === breadcrumb.length - 1 ? 'is-current' : ''}
                    onClick={() => setFolder(breadcrumb.slice(0, index + 1).join('/'))}
                  >
                    {segment}
                  </button>
                </span>
              ))}
            </nav>
            <small>
              {visibleFolders.length} {visibleFolders.length === 1 ? 'folder' : 'folders'} ·{' '}
              {pagination?.total ?? 0} objects
            </small>
            <button className="storage-refresh" type="button" aria-label="Refresh files" disabled={files.isFetching} onClick={() => void files.refetch()}><RefreshCw size={15} /></button>
          <div className="storage-command-actions">
            {canManageFolders && <button type="button" className="storage-new-folder" onClick={() => setCreatingFolder(true)}><Folder size={16} />New folder</button>}
            {canUpload && <div className="storage-upload-control">
              <button type="button" className="storage-upload-button" onClick={() => input.current?.click()}><Upload size={16} />Upload files</button>
              {canPrivacy && <StorageDropdown label="New upload visibility" value={uploadPrivate ? 'private' : 'public'} onChange={value => setUploadPrivate(value === 'private')} icon={uploadPrivate ? <Lock /> : <Globe2 />} menuWidth={220}
                options={[{ value: 'public', label: 'Public uploads' }, { value: 'private', label: 'Private uploads' }]} />}
            </div>}
            <input ref={input} hidden type="file" multiple onChange={event => event.target.files && void handleUpload(event.target.files)} />
          </div>
          </div>
      <nav className="storage-categories" aria-label="File categories">
        {([
          ['all', 'All files', List], ['image', 'Images', FileImage],
          ['video', 'Video', FileVideo], ['audio', 'Audio', FileAudio], ['other', 'Documents', File],
        ] as const).map(([value, label, Icon]) => (
          <button key={value} type="button" className={typeFilter === value ? 'is-active' : ''} aria-pressed={typeFilter === value} onClick={() => setTypeFilter(value)}>
            <Icon size={15} /><span>{label}</span>
          </button>
        ))}
      </nav>
          <div className="file-panel__toolbar">
            <div className="dash-search">
              <Search />
              <input
                aria-label="Search files"
                placeholder="Search this folder · /"
                ref={fileSearchRef}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <StorageDropdown label="Filter by access" value={accessFilter} onChange={setAccessFilter} icon={<Globe2 />}
              options={[{ value: 'all', label: 'All access' }, { value: 'public', label: 'Public' }, { value: 'private', label: 'Private' }]} />
            <StorageDropdown label="Filter by file type" className="storage-type-filter" value={typeFilter} onChange={setTypeFilter} icon={<Filter />}
              options={[{ value: 'all', label: 'All types' }, { value: 'image', label: 'Images' }, { value: 'video', label: 'Video' }, { value: 'audio', label: 'Audio' }, { value: 'other', label: 'Other' }]} />
            <StorageDropdown label="Sort files" value={sort} onChange={setSort} icon={<SlidersHorizontal />}
              options={[{ value: 'newest', label: 'Newest' }, { value: 'oldest', label: 'Oldest' }, { value: 'name', label: 'Name' }, { value: 'size', label: 'Largest' }]} />
            <div className="storage-view-toggle" aria-label="File view">
              <button
                type="button"
                className={view === 'list' ? 'is-active' : ''}
                onClick={() => setView('list')}
                aria-label="List view"
                aria-pressed={view === 'list'}
              >
                <List />
              </button>
              <button
                type="button"
                className={view === 'grid' ? 'is-active' : ''}
                onClick={() => setView('grid')}
                aria-label="Grid view"
                aria-pressed={view === 'grid'}
              >
                <FileImage />
              </button>
            </div>
            {view === 'list' && <button className="storage-density" type="button" aria-label="Compact rows" aria-pressed={compact} onClick={() => setCompact(value => !value)}><SlidersHorizontal size={15} /><span>Compact</span></button>}
            {canWrite && <button type="button" className="storage-settings-button" onClick={() => setPurgeModalOpen(true)} aria-label="Storage settings" title="Storage settings"><Settings size={16} /></button>}
          </div>
          {(search || accessFilter !== 'all' || typeFilter !== 'all') && <div className="storage-active-filters">
            <span>Filtered by</span>
            {search && <button type="button" onClick={() => setSearch('')} aria-label="Clear search">“{search}”<X size={12} /></button>}
            {typeFilter !== 'all' && <button type="button" onClick={() => setTypeFilter('all')} aria-label="Clear file type filter">{typeFilter}<X size={12} /></button>}
            {accessFilter !== 'all' && <button type="button" onClick={() => setAccessFilter('all')} aria-label="Clear access filter">{accessFilter}<X size={12} /></button>}
            <button type="button" className="storage-clear-filters" onClick={() => {setSearch('');setTypeFilter('all');setAccessFilter('all');}}>Clear all</button>
          </div>}
          {selectedIds.size ? (
            <div className="file-selection-bar" role="status">
              <strong>{selectedIds.size} selected</strong>
              <span>
                <button type="button" onClick={() => setSelectedIds(new Set())}>
                  Clear selection
                </button>
                {canDelete && (
                  <button
                    className="button button--dash-quiet button--danger-quiet"
                    type="button"
                    onClick={() => setConfirmBulkDelete(true)}
                  >
                    <Trash2 /> Delete selected
                  </button>
                )}
              </span>
            </div>
          ) : null}
          <div ref={resultsRef} className={selectedFile ? 'storage-results has-inspector' : 'storage-results'}>
            {files.isLoading ? (
              <div className="table-skeleton" aria-label="Loading files" />
            ) : files.isError ? (
              <div className="empty-state file-empty">
                <AlertCircle />
                <h3>Files could not be loaded</h3>
                <p>Check the connection and try again.</p>
                <button className="button button--dash-quiet" onClick={() => void files.refetch()}>
                  Try again
                </button>
              </div>
            ) : (visibleFolders.length || visibleFiles.length) && view === 'list' ? (
              <div className="dash-file-table">
                <div className="dash-file-row dash-file-row--head">
                  <span className="file-select-cell">
                    <input
                      type="checkbox"
                      aria-label="Select every file on this page"
                      checked={allPageSelected}
                      ref={element => { if (element) element.indeterminate = !allPageSelected && visibleFiles.some(file => selectedIds.has(file.id)); }}
                      onChange={() =>
                        setSelectedIds((current) => {
                          const next = new Set(current);
                          if (allPageSelected) visibleFiles.forEach((file) => next.delete(file.id));
                          else visibleFiles.forEach((file) => next.add(file.id));
                          return next;
                        })
                      }
                    />
                  </span>
                  <span>Name</span>
                  <span>Size</span>
                  <span>Type</span>
                  <span>Access</span>
                  <span>Uploaded</span>
                  <span />
                </div>
                {visibleFolders.map((directory) => (
                  <div
                    className="dash-file-row dash-folder-row"
                    key={directory.path}
                    onClick={() => setFolder(directory.path)}
                  >
                    <span className="file-select-cell" />
                    <button type="button" className="dash-file-name storage-file-open" onClick={() => setFolder(directory.path)} aria-label={`Open folder ${directory.name}`}>
                      <Folder />
                      <span>
                        <strong>{directory.name}</strong>
                        <small>{directory.path}</small>
                      </span>
                    </button>
                    <span>{directory.file_count} objects</span>
                    <span>Folder</span>
                    <span>—</span>
                    <span>—</span>
                    <span className="row-actions">
                      {canDeleteFolders && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleteFolderTarget(directory);
                          }}
                          aria-label={`Delete ${directory.name}`}
                        >
                          <Trash2 />
                        </button>
                      )}
                      <ChevronRight />
                    </span>
                  </div>
                ))}
                {visibleFiles.map((file) => (
                  <div
                    className={
                      `dash-file-row${selectedId === file.id ? ' is-selected' : ''}${selectedIds.has(file.id) ? ' is-checked' : ''}`
                    }
                    key={file.id}
                    onClick={() => setSelectedId(file.id)}
                  >
                    <span className="file-select-cell" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${file.original_name}`}
                        checked={selectedIds.has(file.id)}
                        onChange={() => toggleSelected(file.id)}
                      />
                    </span>
                    <button type="button" className="dash-file-name storage-file-open" onClick={() => setSelectedId(file.id)} aria-label={`View details for ${file.original_name}`}>
                      <span className={`storage-file-icon storage-file-icon--${fileCategory(file.mime_type, file.original_name)}`}><FileTypeIcon name={file.original_name} mimeType={file.mime_type} /></span>
                      <span>
                        <strong>{file.original_name}</strong>
                        <small>{file.mime_type.split('/')[1]?.toUpperCase() ?? 'FILE'}<span> · {file.is_private ? 'Private object' : 'CDN ready'}</span></small>
                      </span>
                    </button>
                    <span>{formatBytes(file.size_bytes)}</span>
                    <span>{file.mime_type.split('/')[1] ?? file.mime_type}</span>
                    <span>
                      {canPrivacy ? (
                        <button
                          className={`file-visibility${file.is_private ? ' is-private' : ''}`}
                          type="button"
                          disabled={visibility.isPending}
                          onClick={(event) => {
                            event.stopPropagation();
                            visibility.mutate({ id: file.id, isPrivate: !file.is_private });
                          }}
                          aria-label={`Make ${file.original_name} ${file.is_private ? 'public' : 'private'}`}
                        >
                          {file.is_private ? <Lock /> : <Globe2 />}
                          {file.is_private ? 'Private' : 'Public'}
                        </button>
                      ) : file.is_private ? (
                        'Private'
                      ) : (
                        'Public'
                      )}
                    </span>
                    <span>
                      {new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(
                        new Date(file.created_at),
                      )}
                    </span>
                    <span className="row-actions">
                      <button
                        className={copied === file.id ? 'is-success' : ''}
                        onClick={(event) => {
                          event.stopPropagation();
                          void copyFileLink(file);
                        }}
                        aria-label={file.is_private ? 'Copy private link' : 'Copy CDN URL'}
                      >
                        {copied === file.id ? <Check /> : <Copy />}
                      </button>
                      {canDelete && (
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleteTarget(file);
                          }}
                          aria-label="Delete file"
                        >
                          <Trash2 />
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            ) : visibleFolders.length || visibleFiles.length ? (
              <div className="file-grid">
                {visibleFolders.map((directory) => (
                  <div
                    className="file-tile file-tile--folder"
                    key={directory.path}
                    onClick={() => setFolder(directory.path)}
                  >
                    <span className="file-tile__preview">
                      <Folder />
                    </span>
                    <button type="button" className="file-tile__copy storage-file-open" onClick={() => setFolder(directory.path)} aria-label={`Open folder ${directory.name}`}>
                      <strong>{directory.name}</strong>
                      <small>{directory.file_count} objects</small>
                    </button>
                    <span
                      className="file-tile__folder-actions"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {canDeleteFolders && (
                        <button
                          type="button"
                          onClick={() => setDeleteFolderTarget(directory)}
                          aria-label={`Delete ${directory.name}`}
                        >
                          <Trash2 />
                        </button>
                      )}
                    </span>
                    <ChevronRight className="file-tile__open" />
                  </div>
                ))}
                {visibleFiles.map((file) => (
                  <div
                    className={selectedId === file.id ? 'file-tile is-selected' : 'file-tile'}
                    key={file.id}
                    onClick={() => setSelectedId(file.id)}
                  >
                    <label
                      className="file-tile__select"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Select ${file.original_name}`}
                        checked={selectedIds.has(file.id)}
                        onChange={() => toggleSelected(file.id)}
                      />
                    </label>
                    <span className="file-tile__preview">
                      {!file.is_private && fileCategory(file.mime_type, file.original_name) === 'image' ? (
                        <img src={file.url} alt="" loading="lazy" />
                      ) : (
                        <FileTypeIcon name={file.original_name} mimeType={file.mime_type} />
                      )}
                    </span>
                    <button type="button" className="file-tile__copy storage-file-open" onClick={() => setSelectedId(file.id)} aria-label={`View details for ${file.original_name}`}>
                      <strong>{file.original_name}</strong>
                      <small>{formatBytes(file.size_bytes)} · {file.mime_type.split('/')[1]?.toUpperCase()}</small>
                    </button>
                    <span
                      className={
                        file.is_private ? 'file-tile__access is-private' : 'file-tile__access'
                      }
                    >
                      {file.is_private ? <Lock /> : <Globe2 />}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state file-empty">
                {search || accessFilter !== 'all' || typeFilter !== 'all' ? <Search /> : <Upload />}
                <h3>
                  {search || accessFilter !== 'all' || typeFilter !== 'all'
                    ? 'No files match these filters'
                    : folder
                      ? 'This folder is empty'
                      : 'Drop your first file here'}
                </h3>
                <p>
                  {search || accessFilter !== 'all' || typeFilter !== 'all'
                    ? 'Clear a filter or search for another name.'
                    : workspaceLocked
                      ? 'This workspace is waiting for a paid plan. Uploads stay closed until checkout completes.'
                      : 'Images, clips, voice notes, archives, or backups up to 20 GB.'}
                </p>
                {search || accessFilter !== 'all' || typeFilter !== 'all' ? (
                  <button
                    className="button button--dash-quiet"
                    onClick={() => {
                      setSearch('');
                      setAccessFilter('all');
                      setTypeFilter('all');
                    }}
                  >
                    Clear filters
                  </button>
                ) : canUpload ? (
                    <button
                      className="button button--dash-primary"
                      onClick={() => input.current?.click()}
                    >
                      Choose files
                    </button>
                  ) : workspaceLocked ? (
                    <Link className="button button--dash-primary" to="/pricing">
                      Choose a plan
                    </Link>
                  ) : null}
              </div>
            )}
            {selectedFile ? (
              <aside className="file-inspector" aria-label="File details">
                <div className="file-inspector__head">
                  <div>
                    <span>Object details</span>
                    <strong>{selectedFile.original_name}</strong>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedId('')}
                    aria-label="Close details"
                  >
                    <X />
                  </button>
                </div>
                <div className="file-inspector__preview">
                  {!selectedFile.is_private && fileCategory(selectedFile.mime_type, selectedFile.original_name) === 'image' ? (
                    <img src={selectedFile.url} alt={selectedFile.original_name} />
                  ) : (
                    <FileTypeIcon name={selectedFile.original_name} mimeType={selectedFile.mime_type} />
                  )}
                </div>
                <dl>
                  <div>
                    <dt>Size</dt>
                    <dd>{formatBytes(selectedFile.size_bytes)}</dd>
                  </div>
                  <div>
                    <dt>Type</dt>
                    <dd>{selectedFile.mime_type}</dd>
                  </div>
                  <div>
                    <dt>Access</dt>
                    <dd>{selectedFile.is_private ? 'Private' : 'Public'}</dd>
                  </div>
                  <div>
                    <dt>Folder</dt>
                    <dd>{selectedFile.folder || 'Root'}</dd>
                  </div>
                  <div>
                    <dt>Uploaded</dt>
                    <dd>
                      {new Intl.DateTimeFormat('en', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      }).format(new Date(selectedFile.created_at))}
                    </dd>
                  </div>
                  <div>
                    <dt>SHA-256</dt>
                    <dd>
                      <code title={selectedFile.sha256}>{selectedFile.sha256.slice(0, 16)}...</code>
                    </dd>
                  </div>
                </dl>
                <div className="file-inspector__actions">
                  <button
                    className="button button--dash-primary"
                    onClick={() => void copyFileLink(selectedFile)}
                  >
                    <Copy /> Copy link
                  </button>
                  {canPrivacy && (
                    <button
                      className="button button--dash-quiet"
                      disabled={visibility.isPending}
                      onClick={() =>
                        visibility.mutate({
                          id: selectedFile.id,
                          isPrivate: !selectedFile.is_private,
                        })
                      }
                    >
                      {selectedFile.is_private ? <Globe2 /> : <Lock />}
                      Make {selectedFile.is_private ? 'public' : 'private'}
                    </button>
                  )}
                  {canDelete && (
                    <button
                      className="button button--dash-quiet button--danger-quiet"
                      onClick={() => setDeleteTarget(selectedFile)}
                    >
                      <Trash2 /> Delete object
                    </button>
                  )}
                </div>
              </aside>
            ) : null}
          </div>
          {pagination ? (
            <nav className="file-pagination" aria-label="File pages">
              <span>
                {pagination.total ? (pagination.page - 1) * pagination.per_page + 1 : 0}–
                {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
                {pagination.total} files <span className="storage-page-weight"> · {formatBytes(visibleFiles.reduce((sum, file) => sum + Number(file.size_bytes), 0))} on this page</span>
              </span>
              <div>
                <button
                  type="button"
                  disabled={pagination.page <= 1 || files.isFetching}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft />
                </button>
                <strong>
                  Page {pagination.page} of {pagination.total_pages}
                </strong>
                <button
                  type="button"
                  disabled={pagination.page >= pagination.total_pages || files.isFetching}
                  onClick={() => setPage((current) => current + 1)}
                  aria-label="Next page"
                >
                  <ChevronRight />
                </button>
              </div>
            </nav>
          ) : null}
          <div className="drop-overlay">
            <Upload />
            <strong>Drop to upload into {folder || 'All files'}</strong>
          </div>
        </section>
      </div>

      <ModalPresence>{purgeModalOpen && (
        <div className="secret-modal" role="dialog" aria-modal="true">
          <button className="secret-modal__scrim" onClick={() => setPurgeModalOpen(false)} />
          <div className="purge-dialog">
            <span>
              <Settings />
            </span>
            <h2>Storage auto-purge</h2>
            <p>
              Automatically delete older files (FIFO) when your storage limit is reached so incoming uploads are never blocked.
            </p>

            <label className="purge-toggle-row">
              <input
                type="checkbox"
                checked={purgeEnabled}
                onChange={(e) => setPurgeEnabled(e.target.checked)}
              />
              <div>
                <strong>Enable auto-purge</strong>
                <small>Delete matching old files when quota is reached during uploads.</small>
              </div>
            </label>

            {purgeEnabled && (
              <div className="purge-options">
                <label>
                  Target folder (optional)
                  <input
                    type="text"
                    placeholder="e.g. temp or backups/daily (leave blank for root)"
                    value={purgeFolder}
                    onChange={(e) => setPurgeFolder(e.target.value)}
                  />
                </label>

                <label>
                  Filename match rule
                  <select
                    value={purgeMatchType}
                    onChange={(e) => setPurgeMatchType(e.target.value as any)}
                  >
                    <option value="all">All files in target folder</option>
                    <option value="starts_with">Filename starts with...</option>
                    <option value="contains">Filename contains...</option>
                    <option value="ends_with">Filename ends with (extension)...</option>
                  </select>
                </label>

                {purgeMatchType !== "all" && (
                  <label>
                    Match pattern
                    <input
                      type="text"
                      placeholder={purgeMatchType === "ends_with" ? ".tmp, .log, .mp4" : "prefix_"}
                      value={purgeMatchValue}
                      onChange={(e) => setPurgeMatchValue(e.target.value)}
                    />
                  </label>
                )}
              </div>
            )}

            <div className="dialog-actions">
              <button
                type="button"
                className="button button--dash-quiet"
                onClick={() => setPurgeModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button button--dash-primary"
                disabled={savePurgeMutation.isPending}
                onClick={() => {
                  savePurgeMutation.mutate({
                    enabled: purgeEnabled,
                    folder: purgeFolder.trim(),
                    match_type: purgeMatchType,
                    match_value: purgeMatchValue.trim(),
                  });
                }}
              >
                {savePurgeMutation.isPending ? "Saving..." : "Save settings"}
              </button>
            </div>
          </div>
        </div>
      )}</ModalPresence>
      
      <ModalPresence>{creatingFolder ? (
        <div
          className="secret-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="folder-title"
        >
          <button className="secret-modal__scrim" onClick={() => setCreatingFolder(false)} />
          <form
            className="folder-dialog"
            onSubmit={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              createFolder.mutate(folderName);
            }}
          >
            <span>
              <Folder />
            </span>
            <h2 id="folder-title">New folder</h2>
            <p>
              Create it inside <strong>{folder || 'Storage'}</strong>. You can add more folders
              inside it later.
            </p>
            <label>
              Folder name
              <input
                autoFocus
                maxLength={120}
                value={folderName}
                onChange={(event) => setFolderName(event.target.value)}
                placeholder="e.g. recordings"
              />
            </label>
            <div className="dialog-actions">
              <button
                type="button"
                className="button button--dash-quiet"
                onClick={() => setCreatingFolder(false)}
                disabled={createFolder.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--dash-primary"
                disabled={!folderName.trim() || createFolder.isPending}
              >
                {createFolder.isPending ? 'Creating...' : 'Create folder'}
              </button>
            </div>
          </form>
        </div>
      ) : null}</ModalPresence>
      <ModalPresence>{deleteTarget ? (
        <div
          className="secret-modal"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="delete-file-title"
        >
          <button className="secret-modal__scrim" onClick={() => setDeleteTarget(null)} />
          <div className="revoke-dialog">
            <span>
              <Trash2 />
            </span>
            <h2 id="delete-file-title">Delete {deleteTarget.original_name}?</h2>
            <p>The object and every cached copy will be removed. This action cannot be undone.</p>
            <div className="dialog-actions">
              <button
                className="button button--dash-quiet"
                onClick={() => setDeleteTarget(null)}
                disabled={remove.isPending}
              >
                Cancel
              </button>
              <button
                className="button button--danger"
                onClick={() => remove.mutate(deleteTarget.id)}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Deleting...' : 'Delete object'}
              </button>
            </div>
          </div>
        </div>
      ) : null}</ModalPresence>
      <ModalPresence>{deleteFolderTarget ? (
        <div className="secret-modal" role="alertdialog" aria-modal="true">
          <button className="secret-modal__scrim" onClick={() => setDeleteFolderTarget(null)} />
          <div className="revoke-dialog">
            <span>
              <Trash2 />
            </span>
            <h2>Delete {deleteFolderTarget.name}?</h2>
            <p>
              The folder, every nested folder and all {deleteFolderTarget.file_count} objects inside
              will be permanently removed. This cannot be undone.
            </p>
            <div className="dialog-actions">
              <button
                className="button button--dash-quiet"
                onClick={() => setDeleteFolderTarget(null)}
                disabled={removeFolder.isPending}
              >
                Cancel
              </button>
              <button
                className="button button--danger"
                onClick={() => removeFolder.mutate(deleteFolderTarget.path)}
                disabled={removeFolder.isPending}
              >
                {removeFolder.isPending ? 'Deleting...' : 'Delete folder'}
              </button>
            </div>
          </div>
        </div>
      ) : null}</ModalPresence>
      <ModalPresence>{confirmBulkDelete ? (
        <div className="secret-modal" role="alertdialog" aria-modal="true">
          <button className="secret-modal__scrim" onClick={() => setConfirmBulkDelete(false)} />
          <div className="revoke-dialog">
            <span>
              <Trash2 />
            </span>
            <h2>Delete {selectedIds.size} selected objects?</h2>
            <p>Every selected object and its cached copies will be removed permanently.</p>
            <div className="dialog-actions">
              <button
                className="button button--dash-quiet"
                onClick={() => setConfirmBulkDelete(false)}
                disabled={removeSelected.isPending}
              >
                Cancel
              </button>
              <button
                className="button button--danger"
                onClick={() => removeSelected.mutate([...selectedIds])}
                disabled={removeSelected.isPending}
              >
                {removeSelected.isPending ? 'Deleting...' : 'Delete selected'}
              </button>
            </div>
          </div>
        </div>
      ) : null}</ModalPresence>
    </div>
  );
}

function LogObservatory() {
  const client = useQueryClient();
  const notify = useToast();
  const [stream, setStream] = useState("production");
  const [search, setSearch] = useState("");
  const [eventType, setEventType] = useState("");
  const [entity, setEntity] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [resourceFilter, setResourceFilter] = useState("");
  const [rangeHours, setRangeHours] = useState(24);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [isLiveStreaming, setIsLiveStreaming] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);

  const [showCreateStream, setShowCreateStream] = useState(false);
  const [newStreamName, setNewStreamName] = useState("");
  const [newStreamSlug, setNewStreamSlug] = useState("");
  const [newStreamDesc, setNewStreamDesc] = useState("");

  const rangeMenu = useRef<HTMLDivElement>(null);
  const ranges = [
    { hours: 1, label: "Last hour" },
    { hours: 6, label: "Last 6 hours" },
    { hours: 24, label: "Last 24 hours" },
    { hours: 72, label: "Last 3 days" },
    { hours: 168, label: "Last 7 days" },
    { hours: 720, label: "Last 30 days" },
  ];
  const selectedRangeLabel =
    ranges.find((range) => range.hours === rangeHours)?.label ?? "Last 24 hours";

  useEffect(() => {
    const closeRangeMenu = (event: PointerEvent) => {
      if (!rangeMenu.current?.contains(event.target as Node)) setRangeOpen(false);
    };
    const closeRangeMenuWithKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") setRangeOpen(false);
    };
    document.addEventListener("pointerdown", closeRangeMenu);
    document.addEventListener("keydown", closeRangeMenuWithKeyboard);
    return () => {
      document.removeEventListener("pointerdown", closeRangeMenu);
      document.removeEventListener("keydown", closeRangeMenuWithKeyboard);
    };
  }, []);

  const streams = useQuery({
    queryKey: ["streams"],
    queryFn: () =>
      api<{ streams: Array<{ id: string; name: string; slug: string }> }>("/logs/streams"),
  });

  const query = useQuery({
    queryKey: ["logs", stream, search, eventType, entity, levelFilter, resourceFilter, rangeHours],
    enabled: Boolean(streams.data?.streams?.some((item) => item.slug === stream)),
    refetchInterval: isLiveStreaming ? 4000 : false,
    queryFn: () => {
      const end = new Date();
      const start = new Date(end.getTime() - rangeHours * 60 * 60 * 1000);
      const parameters = new URLSearchParams({
        stream,
        from: start.toISOString(),
        to: end.toISOString(),
      });
      if (search.trim()) parameters.set("search", search.trim());
      if (eventType.trim()) parameters.set("event_type", eventType.trim());
      if (entity.trim()) parameters.set("entity_id", entity.trim());
      if (resourceFilter.trim()) parameters.set("resource", resourceFilter.trim());
      if (levelFilter !== "all") parameters.set("level", levelFilter);
      return api<{ events: any[]; timeline: number[]; scanned_segments: number }>("/logs/query?" + parameters.toString());
    },
  });

  useEffect(() => {
    if (streams.data?.streams?.length && !streams.data.streams.some((item) => item.slug === stream))
      if (streams.data?.streams?.[0]) setStream(streams.data.streams[0].slug);
  }, [stream, streams.data?.streams]);

  const exportEvents = () => {
    const events = query.data?.events ?? [];
    if (!events.length) {
      notify({ title: "There are no matching events to export", tone: "info" });
      return;
    }
    const quote = (value: unknown) => "\"" + String(value ?? "").replaceAll("\"", "\"\"") + "\"";
    const rows = [
      ["timestamp", "level", "resource", "trace_id", "event_type", "entity_id", "message", "payload"].join(","),
      ...events.map((event: any) =>
        [
          event.occurred_at,
          event.level,
          event.resource || "",
          event.trace_id || "",
          event.event_type || "",
          event.entity_id || "",
          event.message || "",
          JSON.stringify(event.payload ?? {}),
        ]
          .map(quote)
          .join(","),
      ),
    ];
    const url = URL.createObjectURL(
      new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" }),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = stream + "-" + new Date().toISOString().slice(0, 10) + ".csv";
    anchor.click();
    URL.revokeObjectURL(url);
    notify({
      title: events.length + " events exported",
      description: "CSV download ready.",
      tone: "success",
    });
  };

  const createStreamMutation = useMutation({
    mutationFn: (body: { name: string; slug: string; description?: string | undefined }) =>
      api("/logs/streams", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["streams"] });
      setShowCreateStream(false);
      setNewStreamName("");
      setNewStreamSlug("");
      setNewStreamDesc("");
      notify({
        title: "Log stream created",
        description: "New stream is now ready for ingestion.",
        tone: "success",
      });
    },
    onError: (err: any) => notify({ title: "Could not create stream", description: err.message, tone: "error" }),
  });

  const levels = [
    { id: "all", label: "All levels" },
    { id: "info", label: "INFO", color: "var(--color-brand)" },
    { id: "warn", label: "WARN", color: "#f59e0b" },
    { id: "error", label: "ERROR", color: "#ef4444" },
    { id: "debug", label: "DEBUG", color: "#8b5cf6" },
  ];

  if (streams.isLoading)
    return (
      <div className="dashboard-page logs-page">
        <div className="dash-heading">
          <div className="dash-heading__copy">
            <p>Log Observatory</p>
            <h1>Live events</h1>
          </div>
        </div>
        <div className="dash-card log-loading-state">
          <DashboardDataSkeleton rows={7} />
        </div>
      </div>
    );

  if (streams.isError)
    return (
      <div className="dashboard-page logs-page">
        <div className="dash-heading">
          <div className="dash-heading__copy">
            <p>Log Observatory</p>
            <h1>Live events</h1>
          </div>
        </div>
        <div className="dash-card empty-state">
          <AlertCircle />
          <h3>Log streams could not be loaded</h3>
          <p>Check the connection and try again.</p>
          <button className="button button--dash-quiet" onClick={() => void streams.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );

  if (!streams.isLoading && !streams.data?.streams?.length)
    return (
      <div className="dashboard-page">
        <div className="dash-heading">
          <div className="dash-heading__copy">
            <p>Log Observatory</p>
            <h1>See what your systems said.</h1>
          </div>
        </div>
        <div className="dash-card empty-onboarding">
          <div className="empty-onboarding__content">
            <span className="empty-onboarding__icon empty-onboarding__icon--plain">
              <Activity />
            </span>
            <div>
              <h2>Create your first log stream</h2>
              <p>
                Keep related application events together, then inspect them from one searchable
                timeline. Fully compatible with standard JSON and Fivemanage V3 logging.
              </p>
            </div>
            <button
              className="button button--dash-primary"
              disabled={createStreamMutation.isPending}
              onClick={() => createStreamMutation.mutate({ name: "Production", slug: "production" })}
            >
              {createStreamMutation.isPending ? "Creating stream..." : "Create production stream"}
            </button>
            <Link to="/docs#ingest">Read the ingestion guide</Link>
          </div>
        </div>
      </div>
    );

  return (
    <div className="dashboard-page logs-page">
      <div className="dash-heading">
        <div className="dash-heading__copy">
          <p>Log Observatory</p>
          <h1>Observability & Telemetry</h1>
        </div>
        <div className="dash-heading__actions" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            className={"button button--dash-quiet " + (isLiveStreaming ? "is-active" : "")}
            onClick={() => setIsLiveStreaming((prev) => !prev)}
            title={isLiveStreaming ? "Pause auto-refresh" : "Resume auto-refresh"}
          >
            {isLiveStreaming ? <Pause /> : <Play />}
            {isLiveStreaming ? "Live" : "Paused"}
          </button>
          <button
            className="button button--dash-quiet"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
            title="Refresh logs now"
          >
            <RefreshCw className={query.isFetching ? "spin" : ""} />
          </button>
          <button
            className="button button--dash-quiet"
            onClick={() => setShowCreateStream(true)}
            title="Create new stream / dataset"
          >
            <FolderPlus />
            New Stream
          </button>
          <button
            className="button button--dash-quiet"
            onClick={exportEvents}
            disabled={query.isLoading || query.isFetching || !query.data?.events?.length}
          >
            <Clipboard />
            Export CSV
          </button>
        </div>
      </div>

      <div className="logs-toolbar">
        <select
          className="logs-toolbar__stream"
          value={stream}
          onChange={(event) => setStream(event.target.value)}
        >
          {streams.data?.streams?.map((item) => (
            <option value={item.slug} key={item.id}>
              {item.name} ({item.slug})
            </option>
          ))}
        </select>

        <div className="logs-toolbar__levels">
          {levels.map((lvl) => (
            <button
              key={lvl.id}
              type="button"
              className={levelFilter === lvl.id ? "is-active" : ""}
              onClick={() => setLevelFilter(lvl.id)}
              style={levelFilter === lvl.id && lvl.color ? { color: lvl.color } : undefined}
            >
              {lvl.label}
            </button>
          ))}
        </div>

        <div className="dash-search">
          <Search />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search body, message or payload JSON..."
          />
        </div>

        <input
          className="logs-toolbar__input"
          value={resourceFilter}
          onChange={(event) => setResourceFilter(event.target.value)}
          placeholder="Resource / script"
          style={{ width: "140px" }}
        />

        <input
          className="logs-toolbar__input"
          value={eventType}
          onChange={(event) => setEventType(event.target.value)}
          placeholder="Event type"
          style={{ width: "120px" }}
        />

        <input
          className="logs-toolbar__input"
          value={entity}
          onChange={(event) => setEntity(event.target.value)}
          placeholder="Entity ID"
          style={{ width: "110px" }}
        />

        <div className="log-range" ref={rangeMenu}>
          <button
            className="log-range__trigger"
            type="button"
            aria-haspopup="menu"
            aria-expanded={rangeOpen}
            onClick={() => setRangeOpen((open) => !open)}
          >
            {selectedRangeLabel} <ChevronDown />
          </button>
          {rangeOpen ? (
            <div className="log-range__menu" role="menu">
              {ranges.map((range) => (
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={range.hours === rangeHours}
                  key={range.hours}
                  onClick={() => {
                    setRangeHours(range.hours);
                    setRangeOpen(false);
                  }}
                >
                  <span>{range.label}</span>
                  {range.hours === rangeHours ? <Check /> : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="dash-card log-display">
        {query.isLoading ? (
          <div className="log-loading-state">
            <DashboardDataSkeleton rows={9} />
          </div>
        ) : query.isError ? (
          <div className="empty-state">
            <AlertCircle />
            <h3>Events could not be loaded</h3>
            <p>Try widening the time window or narrowing your search query.</p>
            <button className="button button--dash-quiet" onClick={() => void query.refetch()}>
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="log-inspector">
              <div className="log-inspector__summary">
                <span>Timeline distribution</span>
                <strong>
                  {query.data?.events?.length ?? 0}
                </strong>
                <small>events matching in current window</small>
              </div>
              <div className="log-inspector__plot">
                <LogTrend
                  timeline={query.data?.timeline ?? []}
                  from={(query.dataUpdatedAt || Date.now()) - rangeHours * 60 * 60 * 1000}
                  to={query.dataUpdatedAt || Date.now()}
                  rangeHours={rangeHours}
                />
              </div>
            </div>

            <div className="dash-log-table">
              <div className="dash-log-row dash-log-row--head">
                <span>Time</span>
                <span>Level</span>
                <span>Resource</span>
                <span>Event / Entity</span>
                <span>Message / Body</span>
                <span style={{ justifyContent: "flex-end" }}>Inspect</span>
              </div>
              {query.data?.events?.length ? (
                query.data.events.map((event: any, index: number) => {
                  const isSelected = selectedEvent === event;
                  return (
                    <div
                      className={"dash-log-row " + (isSelected ? "is-selected" : "")}
                      key={(event.occurred_at || "") + "-" + index}
                      onClick={() => setSelectedEvent(isSelected ? null : event)}
                      style={{ cursor: "pointer", transition: "background 0.15s ease" }}
                    >
                      <time style={{ fontSize: "11px", color: "var(--color-muted)" }}>
                        {new Date(event.occurred_at).toLocaleTimeString("en", { hour12: false })}
                      </time>
                      <span className={"log-level log-level--" + (event.level || "info")}>
                        {event.level || "info"}
                      </span>
                      <span style={{ fontSize: "11px", color: "var(--color-ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {event.resource || "—"}
                      </span>
                      <div style={{ display: "flex", flexDirection: "column", gap: "2px", overflow: "hidden" }}>
                        <strong style={{ fontSize: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--color-ink)" }}>
                          {event.event_type || "log"}
                        </strong>
                        {event.entity_id ? (
                          <code style={{ fontSize: "10px", opacity: 0.7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {event.entity_id}
                          </code>
                        ) : null}
                      </div>
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--color-ink)" }}>
                        {event.message || JSON.stringify(event.payload)}
                      </span>
                      <span style={{ display: "flex", justifyContent: "flex-end", opacity: 0.6 }}>
                        <Eye style={{ width: "14px", height: "14px" }} />
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">
                  <Search />
                  <h3>No matching events</h3>
                  <p>Events appear here within seconds of ingestion.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ModalPresence>{selectedEvent && (
        <div className="secret-modal" role="dialog" aria-modal="true">
          <button className="secret-modal__scrim" onClick={() => setSelectedEvent(null)} />
          <div style={{ width: "min(680px, calc(100% - 32px))" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span className={"log-level log-level--" + (selectedEvent.level || "info")}>
                  {selectedEvent.level || "info"}
                </span>
                <h2 style={{ margin: 0, fontSize: "18px" }}>Log Event Inspector</h2>
              </div>
              <button
                type="button"
                className="button button--dash-quiet"
                style={{ padding: "6px", width: "32px", height: "32px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                onClick={() => setSelectedEvent(null)}
                aria-label="Close inspector"
              >
                <X style={{ width: "16px", height: "16px" }} />
              </button>
            </div>

            <div style={{ maxHeight: "65vh", overflowY: "auto", paddingRight: "4px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "12px", marginBottom: "16px", background: "var(--color-paper)", padding: "12px 14px", borderRadius: "var(--radius-sm)", border: "var(--rule-hair) solid var(--color-rule)" }}>
                <div>
                  <span style={{ fontSize: "11px", color: "var(--color-muted)", display: "block", marginBottom: "3px" }}>Timestamp</span>
                  <strong style={{ fontSize: "12px", color: "var(--color-ink)" }}>{new Date(selectedEvent.occurred_at).toLocaleTimeString("en", { hour12: false }) + " (" + new Date(selectedEvent.occurred_at).toISOString().split("T")[0] + ")"}</strong>
                </div>
                <div>
                  <span style={{ fontSize: "11px", color: "var(--color-muted)", display: "block", marginBottom: "3px" }}>Resource</span>
                  <strong style={{ fontSize: "12px", color: "var(--color-ink)" }}>{selectedEvent.resource || "N/A"}</strong>
                </div>
                <div>
                  <span style={{ fontSize: "11px", color: "var(--color-muted)", display: "block", marginBottom: "3px" }}>Trace ID</span>
                  <span style={{ fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--color-ink-2)", wordBreak: "break-all" }}>{selectedEvent.trace_id || "N/A"}</span>
                </div>
                <div>
                  <span style={{ fontSize: "11px", color: "var(--color-muted)", display: "block", marginBottom: "3px" }}>Event Type</span>
                  <strong style={{ fontSize: "12px", color: "var(--color-ink)" }}>{selectedEvent.event_type || "N/A"}</strong>
                </div>
                <div>
                  <span style={{ fontSize: "11px", color: "var(--color-muted)", display: "block", marginBottom: "3px" }}>Entity ID</span>
                  <span style={{ fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--color-ink-2)", wordBreak: "break-all" }}>{selectedEvent.entity_id || "N/A"}</span>
                </div>
              </div>

              {selectedEvent.message ? (
                <div style={{ marginBottom: "16px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-muted)", display: "block", marginBottom: "6px" }}>Message / Body</span>
                  <div style={{ background: "var(--color-paper)", padding: "10px 14px", borderRadius: "var(--radius-sm)", border: "var(--rule-hair) solid var(--color-rule)", fontFamily: "var(--font-mono)", fontSize: "13px", color: "var(--color-ink)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: "1.5" }}>
                    {selectedEvent.message}
                  </div>
                </div>
              ) : null}

              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-muted)" }}>Payload & Metadata (JSON)</span>
                  <button
                    className="button button--dash-quiet"
                    style={{ padding: "3px 8px", fontSize: "11px" }}
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(selectedEvent.payload ?? {}, null, 2));
                      notify({ title: "JSON Copied to clipboard", tone: "success" });
                    }}
                  >
                    <Copy style={{ width: "12px", height: "12px" }} /> Copy JSON
                  </button>
                </div>
                <pre style={{ background: "var(--color-paper)", padding: "12px 14px", borderRadius: "var(--radius-sm)", border: "var(--rule-hair) solid var(--color-rule)", overflowX: "auto", fontSize: "12px", color: "var(--color-ink)", maxHeight: "240px", lineHeight: "1.5", margin: 0 }}>
                  {JSON.stringify(selectedEvent.payload ?? {}, null, 2)}
                </pre>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
              <button className="button button--dash-quiet" onClick={() => setSelectedEvent(null)} type="button">
                Close
              </button>
            </div>
          </div>
        </div>
      )}</ModalPresence>

      <ModalPresence>{showCreateStream && (
        <div className="secret-modal" role="dialog" aria-modal="true">
          <button className="secret-modal__scrim" onClick={() => setShowCreateStream(false)} />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newStreamName.trim() || !newStreamSlug.trim()) return;
              createStreamMutation.mutate({
                name: newStreamName.trim(),
                slug: newStreamSlug.trim(),
                description: newStreamDesc.trim() || undefined,
              });
            }}
          >
            <span>
              <FolderPlus />
            </span>
            <h2>Create Log Stream</h2>
            <p>Define a dataset stream for log aggregation, filtering, and telemetry.</p>

            <label>
              Stream Display Name
              <input
                type="text"
                minLength={2} value={newStreamName}
                onChange={(e) => {
                  setNewStreamName(e.target.value);
                  if (!newStreamSlug) {
                    setNewStreamSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-").slice(0, 60));
                  }
                }}
                placeholder="e.g. FiveM Production Server"
                required
                maxLength={80}
                style={{
                  padding: "10px 12px",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--color-paper)",
                  color: "var(--color-ink)",
                  border: "var(--rule-hair) solid var(--color-rule)",
                  fontSize: "14px",
                }}
              />
            </label>

            <label style={{ marginTop: "12px" }}>
              Slug / Dataset Identifier
              <input
                type="text"
                pattern="[a-z0-9][a-z0-9_\-]{1,59}" title="Use lowercase letters, numbers, underscores or hyphens, starting with a letter or number." minLength={2} value={newStreamSlug}
                onChange={(e) => setNewStreamSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-").slice(0, 60))}
                placeholder="e.g. production or fivem-srv1"
                required
                maxLength={60}
                style={{
                  padding: "10px 12px",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--color-paper)",
                  color: "var(--color-ink)",
                  border: "var(--rule-hair) solid var(--color-rule)",
                  fontSize: "14px",
                }}
              />
              <small style={{ color: "var(--color-muted)", fontSize: "11px", marginTop: "4px", display: "block" }}>
                Use this slug in X-Fivemanage-Dataset header or in POST /v3/logs or /api/v1/logs/ingest/:slug
              </small>
            </label>

            <label style={{ marginTop: "12px" }}>
              Description (Optional)
              <input
                type="text"
                value={newStreamDesc}
                onChange={(e) => setNewStreamDesc(e.target.value)}
                placeholder="e.g. Main server runtime logs"
                maxLength={200}
                style={{
                  padding: "10px 12px",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--color-paper)",
                  color: "var(--color-ink)",
                  border: "var(--rule-hair) solid var(--color-rule)",
                  fontSize: "14px",
                }}
              />
            </label>

            <div style={{ display: "flex", gap: "12px", marginTop: "24px", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="button button--dash-quiet"
                onClick={() => setShowCreateStream(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--dash-primary"
                disabled={createStreamMutation.isPending || !newStreamName.trim() || !newStreamSlug.trim()}
              >
                {createStreamMutation.isPending ? "Creating..." : "Create Stream"}
              </button>
            </div>
          </form>
        </div>
      )}</ModalPresence>
    </div>
  );
}

function Credentials() {
  const client = useQueryClient();
  const notify = useToast();
  const { user } = useAuth();
  const workspaceLocked = user?.plan_code === 'locked';
  const [revealed, setRevealed] = useState<any>(null);
  const [openMenu, setOpenMenu] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<{
    id: string;
    kind: 'api' | 's3';
    name: string;
  } | null>(null);
  
  const [showCreateApi, setShowCreateApi] = useState(false);
  const [apiName, setApiName] = useState('');
  const [apiScopes, setApiScopes] = useState<string[]>([
    'files:read',
    'files:write',
    'logs:read',
    'logs:write',
  ]);

  const [showCreateS3, setShowCreateS3] = useState(false);
  const [s3Name, setS3Name] = useState('');

  const [viewingApiKey, setViewingApiKey] = useState<any | null>(null);

  const credentials = useQuery({
    queryKey: ['credentials'],
    queryFn: () => api<{ api_keys: any[]; s3_credentials: any[] }>('/credentials'),
  });
  const createApi = useMutation({
    mutationFn: (body: { name: string; scopes: string[] }) =>
      api<any>('/credentials/api-keys', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      setRevealed({ title: 'API key created', secret: data.api_key.token });
      void client.invalidateQueries({ queryKey: ['credentials'] });
      void client.invalidateQueries({ queryKey: ['workspace-activity'] });
      notify({
        title: 'API key created',
        description: 'Copy the secret before closing the dialog.',
        tone: 'success',
      });
    },
    onError: () => notify({ title: 'API key could not be created', tone: 'error' }),
  });
  const createS3 = useMutation({
    mutationFn: (body: { name: string }) =>
      api<any>('/credentials/s3', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      setRevealed({
        title: 'S3 credential created',
        access: data.credential.access_key_id,
        secret: data.credential.secret_access_key,
      });
      void client.invalidateQueries({ queryKey: ['credentials'] });
      void client.invalidateQueries({ queryKey: ['workspace-activity'] });
      notify({
        title: 'S3 credential created',
        description: 'Copy the secret before closing the dialog.',
        tone: 'success',
      });
    },
    onError: () => notify({ title: 'S3 credential could not be created', tone: 'error' }),
  });
  const revoke = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: 'api' | 's3' }) =>
      api(`/credentials/${kind}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      const name = revokeTarget?.name ?? 'Credential';
      setRevokeTarget(null);
      setOpenMenu('');
      void client.invalidateQueries({ queryKey: ['credentials'] });
      void client.invalidateQueries({ queryKey: ['workspace-activity'] });
      notify({
        title: `${name} revoked`,
        description: 'Requests using it will now be rejected.',
        tone: 'success',
      });
    },
    onError: () =>
      notify({
        title: 'Credential could not be revoked',
        description: 'Please try again.',
        tone: 'error',
      }),
  });
  if (credentials.isLoading)
    return (
      <div className="dashboard-page credentials-page">
        <div className="dash-heading">
          <div className="dash-heading__copy">
            <p>Developer access</p>
            <h1>API keys and S3</h1>
          </div>
        </div>
        <div className="credential-loading-grid">
          <div className="dash-card">
            <DashboardDataSkeleton rows={4} />
          </div>
          <div className="dash-card">
            <DashboardDataSkeleton rows={4} />
          </div>
        </div>
      </div>
    );
  if (credentials.isError)
    return (
      <div className="dashboard-page credentials-page">
        <div className="dash-heading">
          <div className="dash-heading__copy">
            <p>Developer access</p>
            <h1>API keys and S3</h1>
          </div>
        </div>
        <div className="dash-card empty-state">
          <AlertCircle />
          <h3>Credentials could not be loaded</h3>
          <p>Nothing has been changed. Try loading developer access again.</p>
          <button className="button button--dash-quiet" onClick={() => void credentials.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  return (
    <div className="dashboard-page credentials-page">
      <div className="dash-heading">
        <div className="dash-heading__copy">
          <p>Developer access</p>
          <h1>API keys and S3</h1>
        </div>
      </div>
      <section className="credential-section">
        <div>
          <h2>REST API keys</h2>
          <p>Scoped tokens for uploads, file management, and log ingestion.</p>
        </div>
        <button className="button button--dash-primary" onClick={() => { setApiName('FiveM Server API'); setShowCreateApi(true); }} disabled={workspaceLocked}>
          <Plus />
          Create API key
        </button>
        <div className="dash-card credential-list">
          {credentials.data?.api_keys.length ? (
            credentials.data.api_keys.map((key) => (
              <div key={key.id} style={{ cursor: 'pointer' }} onClick={() => setViewingApiKey(key)}>
                <span className="credential-icon">
                  <Code2 />
                </span>
                <div>
                  <strong>{key.name}</strong>
                  <small>
                    {key.key_prefix}•••••••• · {key.scopes.join(', ')}
                  </small>
                </div>
                <time>{new Date(key.created_at).toLocaleDateString('en')}</time>
                <span className="credential-actions">
                  <button
                    aria-label={`Actions for ${key.name}`}
                    aria-expanded={openMenu === `api-${key.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenu((current) => (current === `api-${key.id}` ? '' : `api-${key.id}`));
                    }}
                  >
                    <MoreHorizontal />
                  </button>
                  {openMenu === `api-${key.id}` && (
                    <span className="credential-menu" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => {
                          setOpenMenu('');
                          setViewingApiKey(key);
                        }}
                      >
                        <Activity />
                        View usage stats
                      </button>
                      <button
                        onClick={() => {
                          setOpenMenu('');
                          setRevokeTarget({ id: key.id, kind: 'api', name: key.name });
                        }}
                      >
                        <Trash2 />
                        Revoke API key
                      </button>
                    </span>
                  )}
                </span>
              </div>
            ))
          ) : (
            <div className="empty-state small">
              <KeyRound />
              <h3>No API keys</h3>
              <p>Create a key when you are ready to connect an application.</p>
            </div>
          )}
        </div>
      </section>
      <section className="credential-section">
        <div>
          <h2>S3 gateway credentials</h2>
          <p>Dedicated access and secret keys for standard S3 clients and backup tools.</p>
        </div>
        <button className="button button--dash-quiet" onClick={() => { setS3Name('Production S3'); setShowCreateS3(true); }} disabled={workspaceLocked}>
          <Plus />
          Create S3 credential
        </button>
        <div className="dash-card credential-list">
          {credentials.data?.s3_credentials.length ? (
            credentials.data.s3_credentials.map((key) => (
              <div key={key.id}>
                <span className="credential-icon">
                  <ShieldCheck />
                </span>
                <div>
                  <strong>{key.name}</strong>
                  <small>{key.access_key_id}</small>
                </div>
                <time>{new Date(key.created_at).toLocaleDateString('en')}</time>
                <span className="credential-actions">
                  <button
                    aria-label={`Actions for ${key.name}`}
                    aria-expanded={openMenu === `s3-${key.id}`}
                    onClick={() =>
                      setOpenMenu((current) => (current === `s3-${key.id}` ? '' : `s3-${key.id}`))
                    }
                  >
                    <MoreHorizontal />
                  </button>
                  {openMenu === `s3-${key.id}` && (
                    <span className="credential-menu">
                      <button
                        onClick={() => setRevokeTarget({ id: key.id, kind: 's3', name: key.name })}
                      >
                        <Trash2 />
                        Revoke S3 credential
                      </button>
                    </span>
                  )}
                </span>
              </div>
            ))
          ) : (
            <div className="empty-state small">
              <ShieldCheck />
              <h3>No S3 credentials</h3>
              <p>Credentials are shown once when they are created.</p>
            </div>
          )}
        </div>
      </section>

      {/* Create API Key Modal */}
      <ModalPresence>{showCreateApi && (
        <div className="secret-modal" role="dialog" aria-modal="true">
          <button className="secret-modal__scrim" onClick={() => setShowCreateApi(false)} />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!apiName.trim() || !apiScopes.length) return;
              createApi.mutate({ name: apiName.trim(), scopes: apiScopes });
            }}
          >
            <span>
              <Code2 />
            </span>
            <h2>Create REST API Key</h2>
            <p>Assign a recognizable name and grant scoped permissions.</p>

            <label>
              Key Name
              <input
                type="text"
                minLength={2} value={apiName}
                onChange={(e) => setApiName(e.target.value)}
                placeholder="e.g. FiveM Production Server"
                required
                maxLength={80}
                style={{
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-paper)',
                  color: 'var(--color-ink)',
                  border: 'var(--rule-hair) solid var(--color-rule)',
                  fontSize: '14px',
                }}
              />
            </label>

            <label style={{ marginTop: '12px' }}>
              Permissions (Scopes)
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '6px' }}>
                {[
                  { id: 'files:write', label: 'Files: Upload & Delete' },
                  { id: 'files:read', label: 'Files: Read & List' },
                  { id: 'logs:write', label: 'Logs: Ingest' },
                  { id: 'logs:read', label: 'Logs: Read' },
                ].map((scope) => {
                  const checked = apiScopes.includes(scope.id);
                  return (
                    <label
                      key={scope.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 10px',
                        borderRadius: 'var(--radius-sm)',
                        background: checked ? 'rgba(255,255,255,0.06)' : 'transparent',
                        border: 'var(--rule-hair) solid var(--color-rule)',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: '#f4f4f0',
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setApiScopes([...apiScopes, scope.id]);
                          } else {
                            if (apiScopes.length <= 1) return; // at least 1 scope required
                            setApiScopes(apiScopes.filter((s) => s !== scope.id));
                          }
                        }}
                      />
                      {scope.label}
                    </label>
                  );
                })}
              </div>
            </label>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="button button--dash-quiet"
                onClick={() => setShowCreateApi(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--dash-primary"
                disabled={createApi.isPending || !apiName.trim() || !apiScopes.length}
              >
                {createApi.isPending ? 'Generating...' : 'Create API Key'}
              </button>
            </div>
          </form>
        </div>
      )}</ModalPresence>

      {/* Create S3 Credential Modal */}
      <ModalPresence>{showCreateS3 && (
        <div className="secret-modal" role="dialog" aria-modal="true">
          <button className="secret-modal__scrim" onClick={() => setShowCreateS3(false)} />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!s3Name.trim()) return;
              createS3.mutate({ name: s3Name.trim() });
            }}
          >
            <span>
              <ShieldCheck />
            </span>
            <h2>Create S3 Credential</h2>
            <p>Generate S3 Access and Secret keys for external tools or scripts.</p>

            <label>
              Credential Name
              <input
                type="text"
                minLength={2} value={s3Name}
                onChange={(e) => setS3Name(e.target.value)}
                placeholder="e.g. Database Backups / S3 Client"
                required
                maxLength={80}
                style={{
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-paper)',
                  color: 'var(--color-ink)',
                  border: 'var(--rule-hair) solid var(--color-rule)',
                  fontSize: '14px',
                }}
              />
            </label>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="button button--dash-quiet"
                onClick={() => setShowCreateS3(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button button--dash-primary"
                disabled={createS3.isPending || !s3Name.trim()}
              >
                {createS3.isPending ? 'Generating...' : 'Create S3 Credential'}
              </button>
            </div>
          </form>
        </div>
      )}</ModalPresence>

      {/* View API Key Stats Modal */}
      <ModalPresence>{viewingApiKey && (
        <div className="secret-modal" role="dialog" aria-modal="true">
          <button className="secret-modal__scrim" onClick={() => setViewingApiKey(null)} />
          <div style={{ width: 'min(500px, calc(100% - 32px))' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ width: '38px', height: '38px', borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,0.06)' }}>
                <Code2 />
              </span>
              <button
                onClick={() => setViewingApiKey(null)}
                style={{ background: 'transparent', border: 'none', color: '#94958f', cursor: 'pointer' }}
              >
                <X style={{ width: '18px', height: '18px' }} />
              </button>
            </div>

            <h2 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '20px', fontWeight: 600 }}>{viewingApiKey.name}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
              <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>Key prefix:</span>
              <span
                style={{
                  display: 'inline-block',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  padding: '3px 8px',
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: '4px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'var(--color-ink-2)',
                }}
              >
                {viewingApiKey.key_prefix}••••••••
              </span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '12px',
                padding: '14px',
                background: 'rgba(255,255,255,0.02)',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.06)',
                marginBottom: '16px',
              }}
            >
              <div>
                <span style={{ fontSize: '11px', color: 'var(--color-muted)', display: 'block' }}>Total Requests</span>
                <strong style={{ fontSize: '16px', color: '#4ade80' }}>
                  {Number(viewingApiKey.request_count || 0).toLocaleString()}
                </strong>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--color-muted)', display: 'block' }}>Created On</span>
                <strong style={{ fontSize: '13px' }}>
                  {new Date(viewingApiKey.created_at).toLocaleDateString()}
                </strong>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--color-muted)', display: 'block' }}>Last Used</span>
                <strong style={{ fontSize: '13px' }}>
                  {viewingApiKey.last_used_at ? new Date(viewingApiKey.last_used_at).toLocaleString() : 'Never'}
                </strong>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--color-muted)', display: 'block' }}>Last Client IP</span>
                <strong style={{ fontSize: '13px', fontFamily: 'monospace' }}>
                  {viewingApiKey.last_ip || 'N/A'}
                </strong>
              </div>
            </div>

            {viewingApiKey.last_user_agent && (
              <label style={{ fontSize: '12px', color: 'var(--color-muted)', display: 'grid', gap: '4px', marginBottom: '16px' }}>
                Last User Agent
                <code style={{ fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#94958f' }}>
                  {viewingApiKey.last_user_agent}
                </code>
              </label>
            )}

            <label style={{ fontSize: '12px', color: 'var(--color-muted)', display: 'grid', gap: '4px' }}>
              Configured Permissions (Scopes)
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                {viewingApiKey.scopes?.map((s: string) => (
                  <span
                    key={s}
                    style={{
                      padding: '3px 8px',
                      borderRadius: '4px',
                      background: 'rgba(255,255,255,0.06)',
                      fontSize: '11px',
                      fontFamily: 'monospace',
                      color: '#f4f4f0',
                    }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </label>

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                className="button button--dash-quiet"
                style={{ color: '#f87171' }}
                onClick={() => {
                  setRevokeTarget({ id: viewingApiKey.id, kind: 'api', name: viewingApiKey.name });
                  setViewingApiKey(null);
                }}
              >
                <Trash2 style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                Revoke Key
              </button>
              <button
                type="button"
                className="button button--dash-quiet"
                onClick={() => setViewingApiKey(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}</ModalPresence>

      <ModalPresence>{revealed && (
        <div className="secret-modal" role="dialog" aria-modal="true">
          <button className="secret-modal__scrim" onClick={() => setRevealed(null)} />
          <div>
            <span>
              <Check />
            </span>
            <h2>{revealed.title}</h2>
            <p>Copy this now. BCKT will not show the secret again.</p>
            {revealed.access && (
              <label>
                Access key<code>{revealed.access}</code>
              </label>
            )}
            <label>
              Secret<code>{revealed.secret}</code>
            </label>
            <button
              className="button button--dash-primary button--full"
              onClick={() => {
                void navigator.clipboard.writeText(revealed.secret);
                setRevealed(null);
              }}
            >
              <Copy />
              Copy secret and close
            </button>
          </div>
        </div>
      )}</ModalPresence>
      <ModalPresence>{revokeTarget && (
        <div
          className="secret-modal"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="revoke-title"
        >
          <button className="secret-modal__scrim" onClick={() => setRevokeTarget(null)} />
          <div className="revoke-dialog">
            <span>
              <Trash2 />
            </span>
            <h2 id="revoke-title">Revoke {revokeTarget.name}?</h2>
            <p>
              Requests using this credential will stop working immediately. This action cannot be
              undone.
            </p>
            {revoke.error && (
              <p className="form-error">The credential could not be revoked. Please try again.</p>
            )}
            <div className="dialog-actions">
              <button
                className="button button--dash-quiet"
                onClick={() => setRevokeTarget(null)}
                disabled={revoke.isPending}
              >
                Cancel
              </button>
              <button
                className="button button--danger"
                onClick={() => revoke.mutate(revokeTarget)}
                disabled={revoke.isPending}
              >
                {revoke.isPending ? 'Revoking…' : 'Revoke credential'}
              </button>
            </div>
          </div>
        </div>
      )}</ModalPresence>
    </div>
  );
}

function Billing({ user }: { user: User }) {
  const [loading, setLoading] = useState(false);
  const notify = useToast();
  const client = useQueryClient();
  const routeLocation = useLocation();
  const navigate = useNavigate();
  const checkoutHandled = useRef(false);
  const history = useQuery({
    queryKey: ['billing-history'],
    queryFn: () =>
      api<{
        invoices: Array<{
          id: string;
          number: string | null;
          description: string;
          status: string | null;
          currency: string;
          amount_paid: number;
          amount_due: number;
          created_at: string;
          paid_at: string | null;
          hosted_invoice_url: string | null;
          invoice_pdf: string | null;
        }>;
      }>('/billing/history'),
  });

  useEffect(() => {
    const checkout = new URLSearchParams(routeLocation.search).get('checkout');
    if (checkout !== 'success' || checkoutHandled.current) return;
    checkoutHandled.current = true;
    notify({
      title: 'Subscription activated',
      description: 'Your new limits are now available in this workspace.',
      tone: 'success',
    });
    void client.refetchQueries({ queryKey: ['me'] });
    void client.refetchQueries({ queryKey: ['billing-history'] });
    const refreshTimer = window.setTimeout(() => {
      void client.refetchQueries({ queryKey: ['me'] });
      void client.refetchQueries({ queryKey: ['billing-history'] });
    }, 2000);
    navigate('/dashboard/billing', { replace: true });
    return () => window.clearTimeout(refreshTimer);
  }, [client, navigate, notify, routeLocation.search]);

  const portal = async () => {
    setLoading(true);
    try {
      const result = await api<{ url: string }>('/billing/portal', { method: 'POST' });
      location.assign(result.url);
    } catch {
      notify({
        title: 'Billing portal could not be opened',
        description: 'Please try again.',
        tone: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="dashboard-page billing-page">
      <div className="dash-heading">
        <div className="dash-heading__copy">
          <p>Usage and billing</p>
          <h1>Your plan</h1>
        </div>
        <div className="dash-heading__actions">
          <button
            className="button button--dash-quiet"
            disabled={loading || !user.has_subscription || user.subscription_status === 'none'}
            onClick={portal}
          >
            <CreditCard />
            {loading ? 'Opening...' : 'Manage billing'}
          </button>
        </div>
      </div>
      <div className="billing-grid">
        <article className="dash-card plan-card">
          <span>Current plan</span>
          <div>
            <h2>{user.plan_code.replaceAll('_', ' ')}</h2>
            <strong>{user.subscription_status}</strong>
          </div>
          <p>
            {user.plan_code === 'locked'
              ? 'This workspace stays closed until a paid plan is active. Uploads, keys and invites cannot start on the free allowance of this network.'
              : user.plan_code !== 'free' && !user.has_subscription
                ? 'Complimentary plan granted by administrators. To switch to your own billing subscription, choose a plan from pricing.'
                : 'Your quota changes automatically when Stripe confirms a subscription update.'}
          </p>
          {user.pending_plan_code ? (
            <p className="pending-plan-note">
              Change to <strong>{user.pending_plan_code.replaceAll('_', ' ')}</strong> scheduled for{' '}
              {user.pending_plan_effective_at
                ? new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(
                    new Date(user.pending_plan_effective_at),
                  )
                : 'the next renewal'}
              .
            </p>
          ) : null}
          <Link to="/pricing">{user.subscription_status === 'none' ? 'Compare plans' : 'Compare plans'}</Link>
        </article>
        <article className="dash-card usage-detail">
          <h2>Capacity</h2>
          <UsageBar
            label="Media storage"
            used={user.storage_used_bytes}
            limit={user.storage_quota_bytes}
          />
          <UsageBar
            label="Log storage"
            used={user.log_used_bytes}
            limit={user.log_quota_bytes}
            color="var(--color-info)"
          />
          <RequestUsageBar
            used={user.file_requests_count ?? 0}
            planCode={user.plan_code}
          />
        </article>
      </div>
      <div style={{ margin: "24px 0" }}>
        <CustomPlanBuilder user={user} purchasesEnabled={true} annual={true} support={null} />
      </div>
      <div className="dash-card invoice-card">
        <div>
          <h2>Billing history</h2>
          <p>Payments and invoices are read directly from your Stripe billing account.</p>
        </div>
        {history.isLoading ? (
          <DashboardDataSkeleton rows={4} />
        ) : history.isError ? (
          <div className="empty-state small">
            <AlertCircle />
            <h3>Billing history is unavailable</h3>
            <button className="button button--dash-quiet" onClick={() => void history.refetch()}>
              Try again
            </button>
          </div>
        ) : history.data?.invoices.length ? (
          <div className="invoice-list">
            <div className="invoice-row invoice-row--head">
              <span>Invoice</span>
              <span>Date</span>
              <span>Status</span>
              <span>Amount</span>
              <span />
            </div>
            {history.data.invoices.map((invoice) => {
              const amount = invoice.amount_paid || invoice.amount_due;
              return (
                <div className="invoice-row" key={invoice.id}>
                  <span>
                    <strong>{invoice.number ?? invoice.id}</strong>
                    <small>{invoice.description}</small>
                  </span>
                  <time>
                    {new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(
                      new Date(invoice.paid_at ?? invoice.created_at),
                    )}
                  </time>
                  <span className={`invoice-status invoice-status--${invoice.status ?? 'unknown'}`}>
                    {invoice.status ?? 'unknown'}
                  </span>
                  <strong>
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: invoice.currency.toUpperCase(),
                    }).format(amount / 100)}
                  </strong>
                  <span className="invoice-actions">
                    {invoice.hosted_invoice_url ? (
                      <a
                        href={invoice.hosted_invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Open invoice"
                      >
                        <ExternalLink />
                      </a>
                    ) : null}
                    {invoice.invoice_pdf ? (
                      <a
                        href={invoice.invoice_pdf}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Download invoice PDF"
                      >
                        <Download />
                      </a>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state small">
            <CreditCard />
            <h3>No invoices yet</h3>
            <p>Your first Stripe invoice will appear here after payment.</p>
          </div>
        )}
      </div>
    </div>
  );
}


function GlobalUploadProvider({ children }: { children: ReactNode }) {
  const [uploads, setUploads] = useState<GlobalUploadItem[]>([]);
  const notify = useToast();
  const client = useQueryClient();

  const clearUploads = useCallback(() => {
    setUploads((current) => current.filter((item) => item.state === 'uploading'));
  }, []);

  const cancelUpload = useCallback((id: string) => {
    setUploads((current) =>
      current.map((item) => {
        if (item.id === id && item.state === 'uploading') {
          if (item.abort) {
            try { item.abort(); } catch { /* Still mark the upload canceled if transport cleanup fails. */ }
          }
          return { ...item, state: 'aborted', speedMbps: 0 };
        }
        return item;
      })
    );
  }, []);

  const uploadFiles = useCallback(async (selected: FileList | File[], folder = '', isPrivate = false) => {
    const list = Array.from(selected);
    const token = await api<{ token: string }>('/auth/upload-token', { method: 'POST' });
    const uploadBase = import.meta.env.VITE_UPLOAD_URL ?? 'https://upload.bckt.io';

    for (const file of list) {
      const uploadId = `up_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      if (file.size > 20 * 1024 ** 3) {
        setUploads((items) => [...items, { id: uploadId, name: file.name, size: file.size, progress: 0, state: 'error' }]);
        notify({
          title: 'File is too large',
          description: `${file.name} exceeds the 20 GB limit.`,
          tone: 'error',
        });
        continue;
      }

      // Chunked Multipart upload for files > 90 MB
      if (file.size > 90 * 1024 * 1024) {
        const abortController = new AbortController();
        let s3UploadId: string | null = null;
        let s3StorageKey: string | null = null;

        const abortFn = () => {
          abortController.abort();
          if (s3UploadId && s3StorageKey) {
            void fetch(`${uploadBase}/v1/upload/chunked/abort?uploadId=${encodeURIComponent(s3UploadId)}&storageKey=${encodeURIComponent(s3StorageKey)}`, {
              method: 'DELETE',
            }).catch(() => {});
          }
        };

        setUploads((items) => [
          ...items,
          { id: uploadId, name: file.name, size: file.size, progress: 0, speedMbps: 0, state: 'uploading', abort: abortFn },
        ]);

        try {
          const initRes = await fetch(`${uploadBase}/v1/upload/chunked/init`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token.token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              filename: file.name,
              folder,
              total_size: file.size,
              content_type: file.type || 'application/octet-stream',
              is_private: isPrivate,
            }),
            signal: abortController.signal,
          });

          const initData = await initRes.json() as any;
          if (!initRes.ok || !initData.upload_id) {
            throw new Error(initData?.error?.message || 'Could not initiate chunked upload');
          }

          s3UploadId = initData.upload_id;
          s3StorageKey = initData.storage_key;
          const { upload_id, storage_key, file_key } = initData;

          const chunkSize = 20 * 1024 * 1024;
          const totalChunks = Math.ceil(file.size / chunkSize);
          const parts: Array<{ PartNumber: number; ETag: string }> = new Array(totalChunks);

          let uploadedBytes = 0;
          let smoothSpeed = 0;
          let lastTickTime = performance.now();
          let lastTickBytes = 0;

          const CONCURRENCY = Math.min(5, totalChunks);
          let currentChunkIdx = 0;

          const uploadWorker = async () => {
            while (true) {
              if (abortController.signal.aborted) throw new Error("Upload canceled");
              const chunkIdx = currentChunkIdx++;
              if (chunkIdx >= totalChunks) break;

              const partNumber = chunkIdx + 1;
              const start = chunkIdx * chunkSize;
              const end = Math.min(start + chunkSize, file.size);
              const chunkBlob = file.slice(start, end);

              const partData = await new Promise<{ etag: string }>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                const partUrl = `${uploadBase}/v1/upload/chunked/part?uploadId=${encodeURIComponent(upload_id)}&partNumber=${partNumber}&storageKey=${encodeURIComponent(storage_key)}`;
                xhr.open("PUT", partUrl);

                let chunkLoaded = 0;
                xhr.upload.onprogress = (event) => {
                  if (event.lengthComputable) {
                    const delta = event.loaded - chunkLoaded;
                    chunkLoaded = event.loaded;
                    uploadedBytes += delta;

                    const now = performance.now();
                    const elapsed = (now - lastTickTime) / 1000;
                    if (elapsed >= 0.5) {
                      const instantSpeed = ((uploadedBytes - lastTickBytes) / (1024 * 1024)) / elapsed;
                      if (instantSpeed > 0.5) {
                        smoothSpeed = smoothSpeed === 0 ? instantSpeed : smoothSpeed * 0.7 + instantSpeed * 0.3;
                      }
                      lastTickTime = now;
                      lastTickBytes = uploadedBytes;

                      const progress = Math.min(99, Math.round((uploadedBytes / file.size) * 100));
                      setUploads((items) =>
                        items.map((item) =>
                          item.id === uploadId && item.state === "uploading"
                            ? { ...item, progress, speedMbps: Number((smoothSpeed || instantSpeed).toFixed(1)) }
                            : item,
                        ),
                      );
                    }
                  }
                };

                const abortHandler = () => xhr.abort();
                abortController.signal.addEventListener("abort", abortHandler, { once: true });

                xhr.onload = () => {
                  abortController.signal.removeEventListener("abort", abortHandler);
                  if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                      const res = JSON.parse(xhr.responseText);
                      resolve(res);
                    } catch {
                      reject(new Error("Invalid response from upload server"));
                    }
                  } else {
                    try {
                      const res = JSON.parse(xhr.responseText);
                      reject(new Error(res?.error?.message || `Part ${partNumber} failed`));
                    } catch {
                      reject(new Error(`Part ${partNumber} HTTP ${xhr.status}`));
                    }
                  }
                };

                xhr.onerror = () => {
                  abortController.signal.removeEventListener("abort", abortHandler);
                  reject(new Error(`Network error uploading part ${partNumber}`));
                };

                xhr.onabort = () => {
                  reject(new Error("Upload canceled"));
                };

                xhr.send(chunkBlob);
              });

              parts[chunkIdx] = { PartNumber: partNumber, ETag: partData.etag };
            }
          };

          const workers = Array.from({ length: CONCURRENCY }, (_, i) => new Promise<void>((res) => setTimeout(() => res(uploadWorker()), i * 200)));
          await Promise.all(workers);

          setUploads((items) =>
            items.map((item) =>
              item.id === uploadId && item.state === "uploading"
                ? { ...item, progress: 100 }
                : item,
            ),
          );

          const completeRes = await fetch(`${uploadBase}/v1/upload/chunked/complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              upload_id,
              storage_key,
              file_key,
              parts,
              original_filename: file.name,
              folder,
              size: file.size,
              mime_type: file.type || 'application/octet-stream',
              is_private: isPrivate,
            }),
            signal: abortController.signal,
          });

          const completeData = await completeRes.json() as any;
          if (!completeRes.ok) {
            throw new Error(completeData?.error?.message || 'Failed to finalize chunked upload');
          }

          setUploads((items) =>
            items.map((item) =>
              item.id === uploadId && item.state === 'uploading'
                ? { ...item, progress: 100, speedMbps: 0, state: 'done' }
                : item,
            ),
          );
          void client.invalidateQueries({ queryKey: ['files'] });
          void client.invalidateQueries({ queryKey: ['folders'] });
          void client.invalidateQueries({ queryKey: ['me'] });
          void client.invalidateQueries({ queryKey: ['workspace-activity'] });
          notify({ title: 'Upload complete', description: file.name, tone: 'success' });
          continue;
        } catch (err: any) {
          if (abortController.signal.aborted) {
            setUploads((items) =>
              items.map((item) =>
                item.id === uploadId ? { ...item, state: 'aborted', speedMbps: 0 } : item,
              ),
            );
            notify({ title: 'Upload canceled', description: file.name, tone: 'info' });
          } else {
            setUploads((items) =>
              items.map((item) =>
                item.id === uploadId && item.state === 'uploading'
                  ? { ...item, state: 'error', speedMbps: 0 }
                  : item,
              ),
            );
            notify({ title: 'Upload failed', description: err.message || file.name, tone: 'error' });
          }
          continue;
        }
      }

      // Single upload path for files <= 90 MB
      const xhr = new XMLHttpRequest();
      const abortFn = () => {
        try { xhr.abort(); } catch { /* Cancellation can race with a completed request. */ }
      };

      setUploads((items) => [
        ...items,
        { id: uploadId, name: file.name, size: file.size, progress: 0, speedMbps: 0, state: 'uploading', abort: abortFn },
      ]);

      xhr.open(
        'POST',
        `${uploadBase}/v1/upload?folder=${encodeURIComponent(folder)}&private=${isPrivate}`,
      );
      xhr.setRequestHeader('Authorization', `Bearer ${token.token}`);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      try {
        xhr.setRequestHeader('X-File-Name', encodeURIComponent(file.name));
        xhr.setRequestHeader('X-File-Name-Encoding', 'uri-component');
      } catch {
        setUploads((items) =>
          items.map((item) =>
            item.id === uploadId && item.state === 'uploading'
              ? { ...item, state: 'error', speedMbps: 0 }
              : item,
          ),
        );
        notify({
          title: 'Upload could not start',
          description: `${file.name} has an invalid filename.`,
          tone: 'error',
        });
        continue;
      }

      let lastLoaded = 0;
      let lastTime = performance.now();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const now = performance.now();
          const elapsedSec = (now - lastTime) / 1000;
          let currentSpeed = 0;
          if (elapsedSec > 0.3) {
            currentSpeed = ((event.loaded - lastLoaded) / (1024 * 1024)) / elapsedSec;
            lastTime = now;
            lastLoaded = event.loaded;
          }

          setUploads((items) =>
            items.map((item) =>
              item.id === uploadId && item.state === 'uploading'
                ? {
                    ...item,
                    progress: Math.round((event.loaded / event.total) * 100),
                    speedMbps: currentSpeed > 0 ? currentSpeed : item.speedMbps,
                  }
                : item,
            ),
          );
        }
      };

      xhr.onload = () => {
        const state = xhr.status < 300 ? 'done' : 'error';
        setUploads((items) =>
          items.map((item) =>
            item.id === uploadId && item.state === 'uploading'
              ? { ...item, progress: state === 'done' ? 100 : item.progress, speedMbps: 0, state }
              : item,
          ),
        );
        void client.invalidateQueries({ queryKey: ['files'] });
        void client.invalidateQueries({ queryKey: ['folders'] });
        void client.invalidateQueries({ queryKey: ['me'] });
        void client.invalidateQueries({ queryKey: ['workspace-activity'] });
        notify(
          state === 'done'
            ? { title: 'Upload complete', description: file.name, tone: 'success' }
            : { title: 'Upload failed', description: file.name, tone: 'error' },
        );
      };

      xhr.onabort = () => {
        setUploads((items) =>
          items.map((item) =>
            item.id === uploadId ? { ...item, state: 'aborted', speedMbps: 0 } : item,
          ),
        );
        notify({ title: 'Upload canceled', description: file.name, tone: 'info' });
      };

      xhr.onerror = () => {
        setUploads((items) =>
          items.map((item) =>
            item.id === uploadId && item.state === 'uploading'
              ? { ...item, state: 'error', speedMbps: 0 }
              : item,
          ),
        );
        notify({ title: 'Upload failed', description: file.name, tone: 'error' });
      };

      xhr.send(file);
    }
  }, [client, notify]);

  return (
    <GlobalUploadContext.Provider value={{ uploads, uploadFiles, clearUploads, cancelUpload }}>
      {children}
      <GlobalUploadTray />
    </GlobalUploadContext.Provider>
  );
}

function GlobalUploadTray() {
  const { uploads, clearUploads, cancelUpload } = useGlobalUpload();

  return (
    <AnimatePresence>{uploads.length > 0 && <AnimatedSurface key="uploads" variant="toast" origin="bottom right" className="upload-tray">
      <div>
        <strong>Uploads ({uploads.length})</strong>
        <button onClick={clearUploads} title="Clear finished uploads" aria-label="Clear uploads">
          <X />
        </button>
      </div>
      {uploads.map((upload: GlobalUploadItem) => (
        <div key={upload.id} className="upload-tray__item" data-state={upload.state}>
          <span title={upload.name}>
            {upload.state === 'done' ? (
              <Check className="upload-complete-check" style={{ color: '#4ade80' }} />
            ) : upload.state === 'error' ? (
              <X style={{ color: '#f87171' }} />
            ) : upload.state === 'aborted' ? (
              <X style={{ color: 'var(--color-muted)' }} />
            ) : (
              <BcktUploadSpinner size={14} progress={upload.progress} label={`Uploading ${upload.name}`} />
            )}
            {upload.name}
          </span>
          <div className="upload-tray__bar">
            <i style={{ transform: `scaleX(${upload.progress / 100})` }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
            <small style={{ whiteSpace: 'nowrap' }}>
              {upload.state === 'error'
                ? 'Failed'
                : upload.state === 'aborted'
                ? 'Canceled'
                : upload.state === 'done'
                ? '100%'
                : `${upload.progress}%`}
              {upload.state === 'uploading' && upload.speedMbps && upload.speedMbps > 0 ? (
                <span style={{ display: 'block', fontSize: '10px', color: '#4ade80' }}>
                  {upload.speedMbps.toFixed(1)} MB/s
                </span>
              ) : null}
            </small>
            {upload.state === 'uploading' && (
              <button
                type="button"
                onClick={() => cancelUpload(upload.id)}
                title="Cancel upload"
                aria-label="Cancel upload"
                style={{
                  width: '22px',
                  height: '22px',
                  padding: 0,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: '50%',
                  border: 'none',
                  color: '#f87171',
                  cursor: 'pointer',
                }}
              >
                <X style={{ width: '12px', height: '12px' }} />
              </button>
            )}
          </div>
        </div>
      ))}
    </AnimatedSurface>}</AnimatePresence>
  );
}

export function Dashboard() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [menu, setMenu] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  useContentEntrance(contentRef, user ? location.pathname : "loading");
  if (loading)
    return (
      <div className="app-loading">
        <Brand />
        <span />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  const active = location.pathname.endsWith('/admin')
    ? 'Administration'
    : dashboardNav.find((item) => item.path === location.pathname)?.label ?? 'Overview';
  let content = <Overview user={user} />;
  if (location.pathname.endsWith('/storage')) content = <StorageManager />;
  else if (location.pathname.endsWith('/logs')) content = <LogObservatory />;
  else if (location.pathname.endsWith('/credentials')) content = <Credentials />;
  else if (location.pathname.endsWith('/billing')) content = <Billing user={user} />;
  else if (location.pathname.endsWith('/workspace') || location.pathname.endsWith('/people')) content = <WorkspacePeople />;
  else if (location.pathname.endsWith('/affiliates')) content = <Affiliates />;
  else if (location.pathname.endsWith('/settings')) content = <AccountSettings />;
  else if (location.pathname.endsWith('/admin'))
    content = user.admin_permissions?.some((permission) => permission === '*' || permission === 'admin.access')
      ? <AdminPanel />
      : <Navigate to="/dashboard" replace />;
  return (
    <ToastProvider>
      <GlobalUploadProvider>
        <div className="dashboard-shell">
          <DashboardSidebar open={menu} close={() => setMenu(false)} />
          <div
            className={menu ? 'dash-scrim is-open' : 'dash-scrim'}
            onClick={() => setMenu(false)}
          />
          <div ref={contentRef} className={`dashboard-main${location.pathname.endsWith('/storage') ? ' dashboard-main--storage' : ''}`}>
            <DashboardTopbar title={active} openMenu={() => setMenu(true)} />
            <LockedPlanBanner user={user} />
            <StorageGraceBanner user={user} />
            <FairUseGraceBanner user={user} />
            {content}
          </div>
        </div>
      </GlobalUploadProvider>
    </ToastProvider>
  );
}
